<template>
  <div>
    <!--    Breadcrumbs-->
    <breadcrumb style="margin-left: -15px">
      <breadcrumb-item>
        <router-link to="/dashboards" exact>Dashboards</router-link>
      </breadcrumb-item>
      <breadcrumb-item>
        <router-link to="/dashboards/settings" exact>Dashboard settings</router-link>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>{{dashboard.name}}</span>
      </breadcrumb-item>
    </breadcrumb>

    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <div class="row" v-show="!loading">
      <div class="col-12 col-md dashboard-heading">
        <h2 class="dashboard-heading-title">{{dashboard.name}}</h2>
        <p v-if="dashboard.description" class="dashboard-heading-description">{{dashboard.description}}</p>
      </div>
      <div class="col-12 col-md text-md-right dashboard-heading-actions" v-if="dashboard.can_write !== 0">
        <base-button class="mr-2" type="default"  @click="addSectionPrompt()" size="sm">
          <i class="tim-icons icon-simple-add mr-1"></i> Add widget</base-button>
        <base-button class="mr-2"  type="primary" size="sm" v-on:click="goToDashboardSettings()">Done</base-button>
        <el-dropdown>
          <base-button
            size="sm"
            type="primary"
            icon
          > <i class="tim-icons el-icon-more"></i>
          </base-button>
          <template #dropdown>
            <el-dropdown-menu>
              <a class="dropdown-item mt-2 mb-2"  :href="'/dashboards/?id=' + dashboard.id" target="_blank">
                <i class="tim-icons icon-link-72 text-default mr-2"></i> Go to dashboard
              </a>
              <a class="dropdown-item mt-2 mb-2" @click.prevent="settingsDrawerOpen = true" href="#">
                <i class="tim-icons icon-settings-gear-63 text-default mr-2"></i> Dashboard settings
              </a>
              <hr>
              <a class="dropdown-item mt-2 mb-2" @click="duplicateDashboard($event)" href="#">
                <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate dashboard
              </a>
              <a class="dropdown-item mt-2 mb-2" @click="copyToGroupsPrompt($event)" href="#">
                <i class="tim-icons icon-world text-default mr-2"></i> Copy to other groups
              </a>
              <hr>
              <a class="dropdown-item mt-2 mb-2" @click="deleteDashboard($event)" href="#">
                <i class="tim-icons icon-trash-simple text-default mr-2"></i> Delete dashboard
              </a>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
    <div class="row" v-if="filterBarConfig.enabled && filterSchema.length > 0" v-show="!loading">
      <div class="col dashboard-filter-bar">
        <filter-bar :key="'gfb-' + filterBarKey"
                    :filters="filterSchema"
                    :initial-value="filterValues"
                    :loading="filtersLoading"
                    :quick-actions="[{ key: 'reset', label: 'Reset to dashboard defaults', end: true }]"
                    @change="onGlobalFilterChange"
                    @quick-action="onGlobalFilterQuickAction" />
      </div>
    </div>
    <div class="row" v-show="!loading">
      <div class="col">
        <grid-layout :layout.sync="gridLayout"
                     :col-num="gridCols"
                     :row-height="10"
                     :is-draggable="viewportCols === 0"
                     :is-resizable="viewportCols === 0"
                     :vertical-compact="true"
                     :use-css-transforms="true"
                     @layout-updated="onGridLayoutUpdated"
        >
          <grid-item v-for="item in gridLayout"
                     @resize="onWidgetResized"
                     @resized="onWidgetResized"
                     :static="item.static"
                     :x="item.x"
                     :y="item.y"
                     :w="item.w"
                     :h="item.h"
                     :i="item.i"
                     :key="item.i + 'GRID'"
          >
            <div v-if="item.widget_type_id === 4" class="widget-section" :style="{ borderColor: item.highlight_color || '' }">
              <div class="widget-section-text">
                <h3 class="widget-section-title">{{ item.title }}</h3>
                <p v-if="item.label" class="widget-section-caption">{{ item.label }}</p>
              </div>
              <el-dropdown v-if="dashboard.can_write !== 0" class="widget-section-menu">
                <span class="widget-tile-action"><i class="text-white tim-icons el-icon-more"></i></span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <div style="display: flex; flex-direction: column;" class="ml-3">
                      <small style="margin-bottom: 5px;">Highlight color</small>
                      <div style="display: flex; gap: 5px;">
                        <span v-for="c in ['#e14eca', '#1d8cf8', '#ff8d72', '#00f2c3', '#fd5d93']" :key="'SC' + c" v-on:click="setColor(item, c)"
                              :style="{ cursor: 'pointer', backgroundColor: c, borderRadius: '3px', width: '20px', height: '20px', display: 'inline-block' }"></span>
                      </div>
                    </div>
                    <hr>
                    <a class="dropdown-item mt-2 mb-2" @click="editWidget(item, $event)" href="#">
                      <i class="tim-icons icon-pencil text-default mr-2"></i> Configure
                    </a>
                    <a class="dropdown-item mt-2 mb-2" @click="duplicateWidget(item, $event)" href="#">
                      <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate widget
                    </a>
                    <hr>
                    <a class="dropdown-item mt-2 mb-2" @click="deleteWidget(item, $event)" href="#">
                      <i class="tim-icons icon-trash-simple text-default mr-2"></i> Delete widget
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
            <card v-else :style="{'border-top': '3px solid ' + item.highlight_color }"
                  :data-widget-id="'widget-' + item.id"
                  style="border-radius: 3px; overflow: hidden; height: 100%">
              <!-- Widget Loading Indicator -->
              <div v-if="widgetLoadingStates[item.id]" style="display: flex; justify-content: center; align-items: center; height: 100%; min-height: 150px;">
                <div style="text-align: center;">
                  <i class="tim-icons icon-refresh-01 spin-animation" style="font-size: 2rem;"></i>
                  <p class="text-muted mt-2">Loading widget data...</p>
                </div>
              </div>
              <div v-else class="widget-tile-body">
                <div class="row mb-3" v-if="dashboard.can_write !== 0">
                  <div class="col text-right">
                    <el-dropdown>
                      <span class="widget-tile-action"><i class="text-white tim-icons el-icon-more"></i></span>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <div style="display: flex; flex-direction: column;" class="ml-3">
                            <small style="margin-bottom: 5px;">Highlight color</small>
                            <div style="display: flex; gap: 5px;">
                          <span v-on:click="setColor(item, '#e14eca')"
                                style="cursor: pointer; background-color: #e14eca; border-radius: 3px; width: 20px; height: 20px; display: inline-block;">
                          </span>
                              <span v-on:click="setColor(item, '#1d8cf8')"
                                    style="cursor: pointer; background-color: #1d8cf8; border-radius: 3px; width: 20px; height: 20px; display: inline-block;">
                          </span>
                              <div v-on:click="setColor(item, '#ff8d72')"
                                   style="cursor: pointer; background-color: #ff8d72; border-radius: 3px; width: 20px; height: 20px;">
                              </div>
                              <div v-on:click="setColor(item, '#00f2c3')"
                                   style="cursor: pointer; background-color: #00f2c3; border-radius: 3px; width: 20px; height: 20px;">
                              </div>
                              <div v-on:click="setColor(item, '#fd5d93')"
                                   style="cursor: pointer; background-color: #fd5d93; border-radius: 3px; width: 20px; height: 20px;">
                              </div>
                            </div>
                          </div>
                          <hr>
                          <a class="dropdown-item mt-2 mb-2" @click="editWidget(item, $event)" href="#">
                            <i class="tim-icons icon-pencil text-default mr-2"></i> Configure
                          </a>
                          <a class="dropdown-item mt-2 mb-2" @click="duplicateWidget(item, $event)" href="#">
                            <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate widget
                          </a>
                          <a class="dropdown-item mt-2 mb-2" @click="exportWidgetData(item, $event)" href="#">
                            <i class="tim-icons icon-cloud-download-93 text-default mr-2"></i> Export data
                          </a>
                          <hr>
                          <a class="dropdown-item mt-2 mb-2" @click="deleteWidget(item, $event)" href="#">
                            <i class="tim-icons icon-trash-simple text-default mr-2"></i> Delete widget
                          </a>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </div>
                </div>

                <div class="row mt-2" v-if="item.show_range_option && !item.is_all_time">
                  <div class="col" style="display: flex; flex-wrap: wrap;">
                    <base-input v-if="!globalPeriodOn" label="Showing results for" style="max-width: 200px;flex: 1">
                      <el-select
                        class="select-primary mb-3 pagination-select"
                        v-model="item.period" v-on:change="getWidgetData(item)"
                        placeholder="Date range"
                        value-key="id"
                      >
                        <el-option
                          class="select-primary"
                          v-for="(tpt, index) in time_period_types"
                          :key="'IS' + index + j"
                          :label="tpt.name"
                          :value="tpt"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                    <base-input class="ml-2" v-if="!globalPeriodOn && item.period && item.period.id === 16"
                                style="flex: 1" label="Start date">
                      <el-date-picker v-on:input="getWidgetData(item)"
                        style="border: 0; border-width:0px;"
                        type="date"
                        placeholder="From"
                        :format="day_of_week + date_format_cal"
                        v-model="item.start_date"
                        :picker-options="{ firstDayOfWeek: first_day_of_week }"
                      >
                      </el-date-picker>
                    </base-input>
                    <base-input class="ml-2" v-if="!globalPeriodOn && item.period && item.period.id === 16"
                                style="flex: 1" label="End date">
                      <el-date-picker v-on:input="getWidgetData(item)"
                        style="border: 0; border-width:0px;"
                        type="date"
                        placeholder="To"
                        :format="day_of_week + date_format_cal"
                        v-model="item.end_date"
                        :picker-options="{ firstDayOfWeek: first_day_of_week }"
                      >
                      </el-date-picker>
                    </base-input>
                    <base-input class="ml-2" v-if="isCompareEligible(item)" label="Compare" style="max-width: 200px; flex: 1">
                      <el-select
                        class="select-primary mb-3 pagination-select"
                        v-model="item.compare_mode" v-on:change="getWidgetData(item)"
                        placeholder="Compare"
                      >
                        <el-option class="select-primary" label="None" value="none"/>
                        <el-option class="select-primary" label="Previous periods" value="previous_periods"/>
                        <el-option class="select-primary" label="Same period previous years" value="previous_years"/>
                      </el-select>
                    </base-input>
                    <base-input class="ml-2" v-if="isCompareEligible(item) && item.compare_mode !== 'none'" label="Periods" style="max-width: 90px">
                      <el-select
                        class="select-primary mb-3 pagination-select"
                        v-model="item.compare_count" v-on:change="getWidgetData(item)"
                        placeholder="Periods"
                      >
                        <el-option class="select-primary" v-for="c in [2,3,4,5,6,7,8,9,10]" :key="'CC' + c" :label="c" :value="c"/>
                      </el-select>
                    </base-input>
                    <base-input class="ml-2" v-if="isGranularityEligible(item)" label="Group by" style="max-width: 130px; flex: 1">
                      <el-select
                        class="select-primary mb-3 pagination-select"
                        v-model="item.granularity" v-on:change="getWidgetData(item)"
                        placeholder="Group by"
                      >
                        <el-option class="select-primary" label="Automatic" value="auto"/>
                        <el-option class="select-primary" label="Daily" value="day"/>
                        <el-option class="select-primary" label="Weekly" value="week"/>
                        <el-option class="select-primary" label="Monthly" value="month"/>
                      </el-select>
                    </base-input>
                  </div>
                </div>

                <!-- Widget Content Area - grows to fill space -->
                <div class="widget-content-area">
                  <!-- Chart Widget -->
                  <div v-if="item.widget_type_id === 1" class="widget-chart-block">
                  <div class="row">
                    <div class="col">
                      <h4 style="margin-bottom: 0px">{{item.title}}
                        <el-tooltip v-if="isCurrencyScoped(item)" :content="currencyScopedNote(item)" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-warning" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip>
                        <el-tooltip v-if="unappliedGlobalFilters(item).length > 0" :content="unappliedGlobalFilters(item).map(f => f.label + ': ' + f.why).join('. ')" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-muted" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip></h4>
                      <p class="card-title">
                        {{item.label}}
                      </p>
                    </div>
                  </div>
                  <div v-if="item.data && item.data.length > 0" class="widget-chart-area">
                    <line-chart fill v-if="item.chart_type_id === 1" :chartData="item.data"
                                :comparison-series="getComparisonSeries(item)"
                                :granularity="item.applied_granularity"
                                :primary-label="item.comparison ? formatComparisonRange(item.calculated_start_date, item.calculated_end_date) : ''"/>
                    <bar-chart fill v-if="item.chart_type_id === 2 && item.group_by_field !== 'company_group'" :chartData="item.data" :data-format="(item.table_id === 18 || item.table_id === 19) ? 'count' : (item.y_axis_dimension ? item.y_axis_dimension.label : 'count')"
                               :comparison-series="getComparisonSeries(item)"
                               :primary-label="item.comparison ? formatComparisonRange(item.calculated_start_date, item.calculated_end_date) : ''"/>
                    <pie-chart fill v-if="item.chart_type_id === 3" :chartData="item.data" :data-format="(item.table_id === 18 || item.table_id === 19) ? 'count' : (item.y_axis_dimension ? item.y_axis_dimension.label : 'count')"/>
                    <utilization-widget v-if="item.chart_type_id === 4 && item.table_id === 21" :data="item.data" :grouped="!!item.group_by_user"/>
                    <gauge-chart fill :color="item.highlight_color" v-if="item.chart_type_id === 4 && item.table_id !== 21" :chartData="item.data" :caption="item.table_id === 25 ? 'visits converted' : 'slots booked'" :unit="item.table_id === 25 ? 'visits' : 'slots'"/>
                    <heatmap-chart fill :color="item.highlight_color" v-if="item.chart_type_id === 5" :chartData="item.data"/>
                    <funnel-chart fill :color="item.highlight_color" v-if="item.chart_type_id === 6" :chartData="item.data"/>
                    <group-comparison-chart fill :color="item.highlight_color" v-if="item.chart_type_id === 2 && item.group_by_field === 'company_group'" :chartData="item.data" :format="(v) => getAmount(item.data_type, v)"/>
                  </div>
                  <div v-else class="text-center text-muted mt-3">
                    <p>{{ noDataText(item) }}</p>
                  </div>
                </div>

                <!-- Activity Stream Widget -->
                <div v-if="item.widget_type_id === 2" class="widget-activity-block">
                  <div class="row">
                    <div class="col">
                      <h4 style="margin-bottom: 0px">{{item.title}}
                        <el-tooltip v-if="isCurrencyScoped(item)" :content="currencyScopedNote(item)" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-warning" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip>
                        <el-tooltip v-if="unappliedGlobalFilters(item).length > 0" :content="unappliedGlobalFilters(item).map(f => f.label + ': ' + f.why).join('. ')" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-muted" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip></h4>
                      <p class="card-title">
                        {{item.label}}
                      </p>
                    </div>
                  </div>
                  <div v-if="item.data && item.data.length > 0" class="widget-activity-list">
                    <div class="widget-activity-scroll">
                      <b-table :items="item.data"
                               :fields="activityFields(item)"
                               table-class="widget-activity-table">
                        <template v-slot:cell(action)="row">
                          <div v-fit-text :title="row.item.description">{{row.item.description}}</div>
                        </template>
                        <template v-slot:cell(booked_event)="row">
                          <div v-fit-text :title="activityEvent(row.item).label + (activityEvent(row.item).when ? ', ' + activityEvent(row.item).when : '')">
                            <a v-if="activityEvent(row.item).bookingSlotId && hasPermissions('bookings_view')"
                               :href="'/bookings?selected_booking=' + activityEvent(row.item).bookingSlotId"
                               target="_blank" title="Open booking">{{activityEvent(row.item).label}} <i class="tim-icons el-icon-top-right text-primary"></i></a>
                            <span v-else>{{activityEvent(row.item).label}}</span>
                            <span v-if="activityEvent(row.item).when" class="widget-activity-when"> {{activityEvent(row.item).when}}</span>
                            <a v-if="activityEvent(row.item).transactionId && hasPermissions('transactions_view')"
                               :href="'/purchases/transactions/details?id=' + activityEvent(row.item).transactionId"
                               target="_blank" title="Open transaction" class="ml-1"><i class="tim-icons icon-cart text-primary"></i></a>
                          </div>
                        </template>
                        <template v-slot:cell(transaction)="row">
                          <div v-fit-text>
                            <a v-if="activityEvent(row.item).transactionId && hasPermissions('transactions_view')"
                               :href="'/purchases/transactions/details?id=' + activityEvent(row.item).transactionId"
                               target="_blank">{{activityEvent(row.item).label}} <i class="tim-icons el-icon-top-right text-primary"></i></a>
                            <span v-else>{{activityEvent(row.item).label}}</span>
                          </div>
                        </template>
                        <template v-slot:cell(performed_by)="row">
                          <div v-fit-text>{{row.item.user || row.item.customer || 'System'}}</div>
                        </template>
                        <template v-slot:cell(time_of_event)="row">
                          <div v-fit-text>{{getLocalDateTime(row.item.timestamp)}}</div>
                        </template>
                      </b-table>
                    </div>
                  </div>
                  <div v-else class="text-center text-muted mt-3">
                    <p>No recent activity</p>
                  </div>
                </div>

                <!-- Statistic Widget -->
                <div v-if="item.widget_type_id === 3">
                  <div class="row">
                    <div class="col text-right">
                      <h4 v-fit-text>{{item.title}}
                        <el-tooltip v-if="isCurrencyScoped(item)" :content="currencyScopedNote(item)" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-warning" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip>
                        <el-tooltip v-if="unappliedGlobalFilters(item).length > 0" :content="unappliedGlobalFilters(item).map(f => f.label + ': ' + f.why).join('. ')" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-muted" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip></h4>
                    </div>
                  </div>

                  <!-- Single Statistic (default) -->
                  <div v-if="item.data && item.data.length > 0 && item.data[0] && !item.group_by_game && !item.group_by_user && !item.group_by_field">
                    <div class="row">
                      <div class="col text-right">
                        <h1 class="card-title widget-stat-value" v-fit-text>
                          {{getAmount(item.data_type, item.data[0].statistic)}}</h1>
                      </div>
                    </div>
                    <div class="row" v-if="item.comparison && item.comparison.periods.length === 1">
                      <div class="col text-right">
                        <span v-if="getStatDelta(item, item.comparison.periods[0])"
                              :class="getStatDelta(item, item.comparison.periods[0]).up ? 'text-success' : 'text-danger'">
                          <i :class="getStatDelta(item, item.comparison.periods[0]).up ? 'tim-icons el-icon-caret-top' : 'tim-icons el-icon-caret-bottom'"></i>
                          {{ Math.abs(getStatDelta(item, item.comparison.periods[0]).pct) }}%
                        </span>
                        <span v-else class="text-muted">n/a</span>
                        <small class="text-muted ml-1">vs {{ formatComparisonRange(item.comparison.periods[0].start, item.comparison.periods[0].end) }}</small>
                      </div>
                    </div>
                    <div v-if="item.comparison && item.comparison.periods.length > 1" style="max-height: 400px; overflow-y: auto;">
                      <table class="table table-sm">
                        <tbody>
                          <tr v-for="(p, idx) in item.comparison.periods" :key="'cmp-' + idx" style="border-bottom: 1px solid rgba(125,125,125,0.2);">
                            <td class="text-left" style="padding: 8px 5px;">
                              <span style="font-size: 0.9rem;">{{ formatComparisonRange(p.start, p.end) }}</span>
                            </td>
                            <td class="text-right" style="padding: 8px 5px;">
                              <strong style="font-size: 1rem;" v-if="getComparisonStat(p) !== null">{{ getAmount(item.data_type, p.data[0].statistic) }}</strong>
                              <strong style="font-size: 1rem;" v-else class="text-muted">n/a</strong>
                            </td>
                            <td class="text-right" style="padding: 8px 5px; width: 70px;">
                              <span v-if="getStatDelta(item, p)"
                                    :class="getStatDelta(item, p).up ? 'text-success' : 'text-danger'"
                                    style="font-size: 0.85rem;">
                                {{ getStatDelta(item, p).up ? '+' : '' }}{{ getStatDelta(item, p).pct }}%
                              </span>
                              <span v-else class="text-muted" style="font-size: 0.85rem;">n/a</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Grouped Statistics (by game) -->
                  <div v-else-if="item.data && item.data.length > 0 && item.group_by_game" style="max-height: 400px; overflow-y: auto;">
                    <div class="mb-2">
                      <small class="text-white" style="opacity: 0.85">
                        <i class="tim-icons icon-app mr-1"></i>
                        Grouped by event ({{item.data.length}} events)
                      </small>
                    </div>
                    <table class="table table-sm">
                      <tbody>
                        <tr v-for="(stat, idx) in item.data" :key="'stat-' + idx" style="border-bottom: 1px solid rgba(125,125,125,0.2);">
                          <td class="text-left" style="padding: 10px 5px;">
                            <span style="font-size: 0.95rem;">{{stat.game_name}}</span>
                          </td>
                          <td class="text-right" style="padding: 10px 5px;">
                            <strong style="font-size: 1.1rem;">{{getAmount(item.data_type, stat.statistic)}}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Grouped Statistics (by staff member) -->
                  <div v-else-if="item.data && item.data.length > 0 && item.group_by_user" style="max-height: 400px; overflow-y: auto;">
                    <div class="mb-2">
                      <small class="text-white" style="opacity: 0.85">
                        <i class="tim-icons icon-single-02 mr-1"></i>
                        Grouped by staff member ({{item.data.length}} staff)
                      </small>
                    </div>
                    <table class="table table-sm">
                      <tbody>
                        <tr v-for="(stat, idx) in item.data" :key="'stat-user-' + idx" style="border-bottom: 1px solid rgba(125,125,125,0.2);">
                          <td class="text-left" style="padding: 10px 5px;">
                            <span style="font-size: 0.95rem;">{{stat.user_name}}</span>
                          </td>
                          <td class="text-right" style="padding: 10px 5px;">
                            <strong style="font-size: 1.1rem;">{{getAmount(item.data_type, stat.statistic)}}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Grouped Statistics (PM breakdown: status / priority / assignee / category / user / project) -->
                  <div v-else-if="item.data && item.data.length > 0 && item.group_by_field" style="max-height: 400px; overflow-y: auto;">
                    <div class="mb-2">
                      <small class="text-white" style="opacity: 0.85">
                        <i class="tim-icons icon-coins mr-1" v-if="item.group_by_field === 'currency'"></i><i class="tim-icons icon-chart-bar-32 mr-1" v-else></i>
                        Grouped by {{item.group_by_field}} ({{item.data.length}})
                      </small>
                    </div>
                    <table class="table table-sm">
                      <tbody>
                        <tr v-for="(stat, idx) in item.data" :key="'stat-field-' + idx" style="border-bottom: 1px solid rgba(125,125,125,0.2);">
                          <td class="text-left" style="padding: 10px 5px;">
                            <span style="font-size: 0.95rem;">{{stat.label}}</span>
                          </td>
                          <td class="text-right" style="padding: 10px 5px;">
                            <strong style="font-size: 1.1rem;">{{ stat.currency ? formatCurrencyIn(stat.currency, stat.statistic) : getAmount(item.data_type, stat.statistic) }}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div v-else class="text-center text-muted mt-3">
                    <p>{{ noDataText(item) }}</p>
                  </div>
                  <div class="row">
                    <div class="col text-right">
                      <p class="card-title widget-stat-label" v-fit-text>
                        {{item.label}}
                      </p>
                    </div>
                  </div>
                </div>

                </div>

                <!-- Widget Description - Always at bottom -->
                <div v-if="item.show_description_label !== false" class="widget-desc-footer" :title="getWidgetDescription(item)">
                  <small v-fit-text="{ short: shortenWidgetDescription(getWidgetDescription(item)) }">{{getWidgetDescription(item)}}</small>
                </div>

              </div>
            </card>
          </grid-item>
        </grid-layout>
      </div>
    </div>


    <!-- Export Widget Data Modal -->
    <modal :show.sync="modals.export"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-sm">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4 class="card-title">Export widget data</h4>

        <div class="row">
          <div class="col">
            <base-radio v-model="exportFormat" name="csv" class="mb-3">
              CSV (Comma Separated Values)
            </base-radio>
            <base-radio v-model="exportFormat" name="json" class="mb-3">
              JSON (JavaScript Object Notation)
            </base-radio>
            <base-radio v-model="exportFormat" name="png" class="mb-3">
              PNG (Image)
            </base-radio>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col text-center">
            <base-button type="secondary" @click="modals.export = false">Cancel</base-button>
            <base-button class="ml-3" type="primary" @click="performExport()">Export</base-button>
          </div>
        </div>

      </card>
    </modal>

<!--    Update widget-->
    <base-drawer
      :show="modals.new_widget"
      :title="widget.hasOwnProperty('id') ? 'Edit widget' : 'Add a new widget'"
      @close="modals.new_widget = false">

      <template #header-actions>
        <button v-if="aiWidgetGenerationEnabled && !isCompanyScope" type="button" class="ai-trigger" :class="{ 'ai-trigger--active': showAiAssistant }" @click="showAiAssistant = !showAiAssistant">
          <span class="ai-trigger__label">Generate with AI</span>
          <span class="ai-trigger__beta">Beta</span>
        </button>
      </template>

      <widget-ai-prompt v-if="aiWidgetGenerationEnabled && showAiAssistant && !isCompanyScope" :dashboard-id="$route.query.id" @generated="applyConfigToForm" @close="showAiAssistant = false" />

      <drawer-section v-if="!widget.id" title="Templates" icon="icon-components">
        <div v-if="widgetTemplatesLoading" class="widget-type-grid widget-type-grid--compact">
          <div v-for="n in 8" :key="'WTSK' + n" class="widget-type-card" style="pointer-events: none">
            <span class="skel skel--dot"></span>
            <span class="skel skel--line-sm" style="width: 70%"></span>
          </div>
        </div>
        <div v-else class="widget-type-grid widget-type-grid--compact">
          <button
            type="button"
            class="widget-type-card"
            :class="{ 'widget-type-card--active': selectedWidgetTemplateId === null }"
            @click="selectBlankWidget()">
            <i class="tim-icons icon-simple-add"></i>
            <span>From scratch</span>
          </button>
          <button
            v-for="t in widgetTemplates"
            :key="'WTPL' + t.id"
            type="button"
            class="widget-type-card"
            :class="{ 'widget-type-card--active': selectedWidgetTemplateId === t.id }"
            :title="t.description"
            @click="applyWidgetTemplate(t)">
            <i class="tim-icons" :class="t.icon || 'icon-components'"></i>
            <span>{{ t.name }}</span>
          </button>
        </div>
      </drawer-section>

      <drawer-section title="Details" icon="icon-align-left-2">
        <div class="row">
          <div class="col">
            <base-input placeholder="Title" v-model="widget.title" label="Widget title (optional)">
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input placeholder="Label" v-model="widget.label" label="Widget label (optional)">
            </base-input>
          </div>
        </div>
      </drawer-section>

      <drawer-section title="Widget configuration" icon="icon-settings-gear-63">

        <div class="row" v-if="!widget.widget_type || (widget.widget_type.id !== 2 && widget.widget_type.id !== 4)">
          <div class="col">
            <label class="widget-field-label">Scope *</label>
            <div class="mb-3">
              <segmented-toggle variant="pills" :value="widget.scope" :options="scopeOptions" @input="onScopeChange" />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <label class="widget-field-label">Widget type *</label>
            <div class="widget-type-grid widget-type-grid--equal">
              <button
                v-for="(wt, index) in visibleWidgetTypes"
                :key="'WTB' + index"
                type="button"
                class="widget-type-card"
                :class="{ 'widget-type-card--active': widget.widget_type && widget.widget_type.id === wt.id }"
                @click="selectWidgetType(wt)">
                <i class="tim-icons" :class="widgetTypeIcon(wt.id)"></i>
                <span>{{ wt.name }}</span>
              </button>
            </div>
            <div v-if="errs.widget_type" class="text-danger mt-1" style="font-size: 0.8rem">{{ errs.widget_type }}</div>
          </div>
        </div>

        <div class="row" v-if="widget.widget_type && widget.widget_type.id === 2">
          <div class="col">
            <base-input label="Activity source" required :error="errs.activity_source">
              <el-select
                v-model="widget.activity_source"
                class="select-primary"
                value-key="id" v-on:change="j++"
                placeholder="Activity source">
                <el-option
                  v-for="(acs, index) in activity_sources"
                  class="select-primary"
                  :label="acs.name"
                  :value="{id: acs.id,
                          name: acs.name}"
                  :key="'acs' + index + j "
                ></el-option>
              </el-select>
            </base-input>
          </div>
          <div class="col">
            <base-input label="Number of events to show" :error="errs.number_of_events" placeholder="Event limit" v-model="widget.event_limit" type="number" min="1" max="100" required>
            </base-input>
          </div>
        </div>

        <div class="row" v-if="widget.widget_type && widget.widget_type.id !== 2 && widget.widget_type.id !== 4">
          <div class="col">
            <base-input label="Data source" required :error="errs.data_source">
              <el-select
                v-model="widget.source"
                class="select-primary"
                value-key="id" v-on:change="updateFields()"
                placeholder="Source">
                <el-option
                  v-for="(s, index) in visibleSources"
                  class="select-primary"
                  :label="s.name"
                  :value="{id: s.id,
                          name: s.name}"
                  :key="'S' + index + j"
                ></el-option>
              </el-select>
            </base-input>

          </div>
        </div>

        <div class="row" v-if="widget.source && widget.widget_type.id !== 2 && !isCustomFieldSource(widget.source) && !(isResourceSource(widget.source) && widget.widget_type.id === 1 && widget.chart_type && widget.chart_type.id === 4)">
          <div class="col">
            <base-input label="Field" required :error="errs.field">
              <el-select
                v-model="widget.field"
                class="select-primary"
                value-key="id" v-on:change="updateMetricTypes()"
                placeholder="Source">
                <el-option
                  v-for="(f, index) in fields"
                  class="select-primary"
                  :label="f.name"
                  :value="{id: f.id, data_type: f.data_type, table_id: f.table_id,
                          name: f.name, field_name: f.field_name}"
                  :key="'F' + index + j"
                ></el-option>
              </el-select>
            </base-input>

          </div>
        </div>

        <div class="row" v-if="showUtmLevel">
          <div class="col">
            <base-input label="Breakdown level">
              <el-select
                v-model="widget.utm_level"
                class="select-primary"
                popper-class="otc-select-popper"
                placeholder="Source"
                v-on:change="j++">
                <el-option
                  v-for="o in utmLevelOptions"
                  class="select-primary"
                  :key="'UL' + o.value"
                  :label="o.label"
                  :value="o.value"
                ></el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="row" v-if="showCurrencyGrouping">
          <div class="col">
            <el-switch active-color="#e14eca" v-model="groupByCurrency" />
            <label class="text-white"> &nbsp; One value per currency
              <el-popover trigger="hover" placement="top" popper-class="otc-pop">
                <div>Shows one amount per currency instead of a single total, so groups that use different currencies can be combined. Each amount is shown in its own currency.</div>
                <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
              </el-popover>
            </label>
          </div>
        </div>

        <div class="row" v-if="isCompanyScope">
          <div class="col">
            <base-input label="Groups">
              <el-select v-model="widget.company_group_ids" class="select-primary" style="width: 100%" multiple filterable
                         placeholder="All groups" :key="'groups-' + j">
                <el-option v-for="g in companyGroupOptions" :key="'CG' + g.id" class="select-primary"
                           :label="g.name + (isMoneyField ? ' (' + g.currency + ')' : '')" :value="g.id"
                           :disabled="isGroupDisabledForCurrency(g)" />
              </el-select>
            </base-input>
            <div class="d-flex align-items-center flex-wrap" style="gap: 6px" v-if="allGroups.length > 0">
              <el-dropdown trigger="click" v-if="locationPresets.length > 0">
                <base-button variant="outlined" tone="primary" size="sm"><i class="tim-icons icon-pin"></i>Select by location</base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <template v-for="p in locationPresets">
                      <hr v-if="p.divided" :key="p.key + '-hr'" class="my-1">
                      <a :key="p.key" class="dropdown-item" href="#" @click.prevent="applyLocationPreset(p)">{{ p.label }} ({{ p.ids.length }})</a>
                    </template>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              <base-button variant="outlined" tone="primary" size="sm" @click="selectAllGroups"><i class="tim-icons icon-check-2"></i>Select all</base-button>
              <base-button v-if="widget.company_group_ids && widget.company_group_ids.length > 0" variant="outlined" tone="primary" size="sm" @click="$set(widget, 'company_group_ids', [])"><i class="tim-icons icon-simple-remove"></i>Clear selection</base-button>
            </div>
            <small class="text-white" style="opacity: 0.7" v-if="isMoneyField && !groupByCurrency">Money metrics can only combine groups that share one currency.</small>
          </div>
        </div>

        <div class="row" v-if="widget.source && isCustomFieldSource(widget.source) && widget.widget_type && (widget.widget_type.id === 1 || widget.widget_type.id === 3)">
          <div class="col">
            <base-input label="Custom field" required :error="errs.custom_field">
              <div v-if="customFieldsLoading" class="cf-stats__loading">
                <i class="el-icon-loading cf-stats__spinner"></i>
                <span>Loading custom fields...</span>
              </div>
              <el-select
                v-else
                v-model="widget.custom_field_id"
                class="select-primary"
                placeholder="Select a custom field"
                popper-class="otc-select-popper"
                :key="'cf-select-' + j">
                <el-option
                  v-for="(cf, index) in customFieldOptions"
                  class="select-primary"
                  :label="cf.label"
                  :value="cf.id"
                  :key="'CF' + index + j"
                ></el-option>
              </el-select>
            </base-input>
            <small v-if="!customFieldsLoading && customFieldOptions.length === 0" class="text-white" style="opacity: 0.7">
              No dropdown or checkbox custom fields found for this location.
            </small>
          </div>
        </div>

        <div class="row" v-if="widget.widget_type && widget.widget_type.id === 1">
          <div class="col">
            <label class="widget-field-label">{{ isResourceSource(widget.source) ? 'Visualization *' : 'Chart type *' }}</label>
            <div class="widget-type-grid widget-type-grid--equal">
              <button
                v-for="(ct, index) in visibleChartTypes"
                :key="'CTB' + index"
                type="button"
                class="widget-type-card"
                :class="{ 'widget-type-card--active': widget.chart_type && widget.chart_type.id === ct.id }"
                @click="selectChartType(ct)">
                <i class="tim-icons" :class="chartTypeIcon(ct.id)"></i>
                <span>{{ ct.name }}</span>
              </button>
            </div>
            <div v-if="errs.chart_type" class="text-danger mt-1" style="font-size: 0.8rem">{{ errs.chart_type }}</div>
          </div>
        </div>

        <div class="row" v-if="isCompanyScope && widget.chart_type && widget.chart_type.id === 2 && widget.source && [1, 2, 15, 25, 26].includes(widget.source.id)">
          <div class="col">
            <el-switch active-color="#e14eca" v-model="splitByGroup" />
            <label class="text-white"> &nbsp; Split by group
              <el-popover trigger="hover" placement="top" popper-class="otc-pop">
                <div>One bar per group in the selection, sorted, with a marker at the average of the groups shown. Uses a numeric field of the source instead of a breakdown field.</div>
                <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
              </el-popover>
            </label>
          </div>
        </div>

        <div class="row" v-if="showMeasurement()">
          <div class="col">
            <base-input label="Measurement" required>
              <el-select
                v-model="widget.y_axis_dimension"
                class="select-primary"
                value-key="label"
                placeholder="Measurement"
                :key="'measurement-' + (widget.source ? widget.source.id : 0) + '-' + j">
                <el-option
                  v-for="(m, index) in currentMeasurements"
                  class="select-primary"
                  :label="m.name"
                  :value="{label: m.label,
                          name: m.name}"
                  :key="'MM' + (widget.source ? widget.source.id : 0) + '-' + index + '-' + j"
                ></el-option>
              </el-select>
            </base-input>

          </div>
        </div>

        <div class="row mt-2" v-if="widget.widget_type &&
        (widget.widget_type.id === 3) && !isResourceSource(widget.source)">
          <div class="col">
            <base-input required label="Metric type" :error="errs.metric_type">
              <el-select
                v-model="widget.metric_type"
                class="select-primary"
                value-key="id" v-on:change="errs.metric_type = ''; j++"
                placeholder="Type">
                <el-option
                  v-for="(wmt, index) in metric_types"
                  class="select-primary"
                  :label="wmt.name"
                  :value="{id: wmt.id,
                          name: wmt.name}"
                  :key="'wmt' + index + j"
                ></el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="row mt-2" v-if="widget.source && widget.source.id === 4">
          <div class="col">
            <el-switch active-color="#e14eca"
                       v-model="widget.include_inactive_gift_cards" />
            <label> &nbsp; Include inactive gift cards</label>
          </div>
        </div>

      </drawer-section>

      <drawer-section title="Time range" icon="icon-calendar-60" v-if="showTimeRangeSection()">
          <div>

          <div class="row mt-2">
            <div class="col-12 mb-2" v-if="!isCompanyScope && (widget.widget_type.id === 3 ||
            (widget.hasOwnProperty('chart_type') && widget.chart_type && widget.chart_type.id !== 1))">
              <div class="toggle-field">
                <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.is_all_time" />
                <label>Show all records</label>
              </div>
            </div>
            <div class="col-12" v-if="!widget.is_all_time">
              <div class="toggle-field">
                <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.show_range_option" />
                <label>Show range option picker on dashboard</label>
              </div>
            </div>
            <div class="col-12 mt-2" v-if="!widget.is_all_time && widget.show_range_option && isBuilderCompareEligible()">
              <div class="toggle-field">
                <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.show_compare_option" />
                <label>Show compare option on dashboard</label>
              </div>
            </div>
            <div class="col-12 mt-3" v-if="!widget.is_all_time && widget.show_range_option && isBuilderCompareEligible() && widget.show_compare_option !== false">
              <base-input label="Default compare" class="mb-0">
                <el-select
                  class="select-primary pagination-select"
                  v-model="widget.default_compare_mode" v-on:change="j++"
                  placeholder="Default compare"
                >
                  <el-option class="select-primary" label="None" value="none"/>
                  <el-option class="select-primary" label="Previous periods" value="previous_periods"/>
                  <el-option class="select-primary" label="Same period previous years" value="previous_years"/>
                </el-select>
              </base-input>
            </div>
            <div class="col-12 mt-3" v-if="!widget.is_all_time && widget.show_range_option && isBuilderCompareEligible() && widget.show_compare_option !== false && widget.default_compare_mode !== 'none'">
              <base-input label="Default periods" class="mb-0">
                <el-select
                  class="select-primary pagination-select"
                  v-model="widget.default_compare_count" v-on:change="j++"
                  placeholder="Default periods"
                >
                  <el-option class="select-primary" v-for="c in [2,3,4,5,6,7,8,9,10]" :key="'DCC' + c" :label="c" :value="c"/>
                </el-select>
              </base-input>
            </div>
            <!-- Deliberately not gated on show_range_option: with the picker hidden the
                 dashboard sends no granularity param and the lambda uses this default. -->
            <div class="col-12 mt-3" v-if="!widget.is_all_time && widget.widget_type && widget.widget_type.id === 1 && widget.chart_type && widget.chart_type.id === 1">
              <base-input label="Default grouping" class="mb-0">
                <el-select
                  class="select-primary pagination-select"
                  v-model="widget.default_granularity" v-on:change="j++"
                  placeholder="Default grouping"
                >
                  <el-option class="select-primary" label="Automatic" value="auto"/>
                  <el-option class="select-primary" label="Daily" value="day"/>
                  <el-option class="select-primary" label="Weekly" value="week"/>
                  <el-option class="select-primary" label="Monthly" value="month"/>
                </el-select>
              </base-input>
            </div>
          </div>
        </div>

        <div class="time-range-controls" v-if="!widget.is_all_time && widget.widget_type && widget.widget_type.id !== 2">
          <div class="row mt-3">
            <div class="col-12">
              <base-input required label="Display records in range" :error="errs.date_range">
                <el-select
                  class="select-primary pagination-select"
                  v-model="widget.period" v-on:change="j++"
                  placeholder="Date range"
                  value-key="id"
                >
                  <el-option
                    class="select-primary"
                    v-for="(item, index) in time_period_types"
                    :key="'I' + index + j"
                    :label="item.name"
                    :value="{ name : item.name, id: item.id}"
                  >
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          <div class="row mt-2" v-if="widget.period && widget.period.id === 16">
            <div class="col-lg-6">
              <base-input label="Start date">
                <el-date-picker
                  style="border: 0; border-width:0px;"
                  type="date"
                  placeholder="From"
                  :format="day_of_week + date_format_cal"
                  v-model="widget.start_date"
                  :picker-options="{ firstDayOfWeek: first_day_of_week }"
                >
                </el-date-picker>
              </base-input>
            </div>
            <div class="col-lg-6">
              <base-input label="End date">
                <el-date-picker
                  style="border: 0; border-width:0px;"
                  type="date"
                  placeholder="To"
                  :format="day_of_week + date_format_cal"
                  v-model="widget.end_date"
                  :picker-options="{ firstDayOfWeek: first_day_of_week }"
                >
                </el-date-picker>
              </base-input>
            </div>
          </div>
        </div>

      </drawer-section>

      <drawer-section title="Display options" icon="icon-bullet-list-67" v-if="!widget.widget_type || widget.widget_type.id !== 4">

        <div class="row mt-2">
          <div class="col-12">
            <div class="toggle-field">
              <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.show_description_label" />
              <label>Show widget info label</label>
            </div>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12">
            <div class="toggle-field">
              <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.allow_export" />
              <label>Allow data export on dashboard view</label>
            </div>
          </div>
        </div>

      </drawer-section>

      <drawer-section title="Filtering options" icon="icon-zoom-split" v-if="!(widget.widget_type && widget.widget_type.id === 4) && (isCompanyScope ? (widget.source && (widget.source.id === 2 || widget.source.id === 15 || widget.source.id === 17)) || showPromoFilter || showEmailTemplateFilter || showUtmFilter : (showGameFilter() || showCompanyUserFilter() || showPmBreakdown() || (widget.source && (widget.source.id === 2 || widget.source.id === 15 || widget.source.id === 17)) || showPromoFilter || showEmailTemplateFilter || showUtmFilter))">
          <div>

          <!-- PM breakdown - For Statistics Widgets (work tickets / time logs) -->
          <div class="row mt-2" v-if="!isCompanyScope && widget.widget_type && widget.widget_type.id === 3 && showPmBreakdown()">
            <div class="col-12">
              <base-input label="Breakdown">
                <el-select
                  v-model="widget.group_by_field"
                  class="select-primary"
                  placeholder="Select breakdown"
                  clearable
                >
                  <el-option
                    v-for="(opt, i) in pmBreakdownOptions()"
                    :key="'pmbd-' + i"
                    class="select-primary"
                    :label="opt.label"
                    :value="opt.value"
                  ></el-option>
                </el-select>
              </base-input>
              <info-hint variant="primary" style="width: 100%">
                Leave this empty for a single total, or pick a dimension to split the statistic into a ranked table, for example ticket counts grouped by <strong>status</strong>, <strong>priority</strong>, or <strong>assignee</strong>.
              </info-hint>
            </div>
          </div>

          <!-- Game Filter - For Statistics Widgets -->
          <div class="row mt-2" v-if="!isCompanyScope && widget.widget_type && widget.widget_type.id === 3 && showGameFilter()">
            <div class="col-12">
              <base-input label="Event filtering">
                <el-select
                  v-model="gameFilterMode"
                  class="select-primary"
                  placeholder="Select filtering option"
                >
                  <el-option
                    class="select-primary"
                    label="All events"
                    value="all"
                  ></el-option>
                  <el-option
                    class="select-primary"
                    label="Filter by event"
                    value="filter"
                  ></el-option>
                  <el-option
                    class="select-primary"
                    label="Show breakdown by event"
                    value="breakdown"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <!-- Game dropdown - only shown when "Filter by game" is selected -->
            <div class="col-12" v-if="gameFilterMode === 'filter'">
              <base-input label="Select event">
                <el-select
                  v-model="widget.game_id"
                  class="select-primary"
                  placeholder="Select an event"
                  v-on:change="j++"
                >
                  <el-option
                    v-for="(game, index) in games"
                    class="select-primary"
                    :key="'GM' + index + j"
                    :label="game.name"
                    :value="game.id"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
          </div>

          <!-- Game Filter - For Charts and Other Widgets -->
          <div class="row mt-2" v-if="!isCompanyScope && widget.widget_type && widget.widget_type.id !== 2 && widget.widget_type.id !== 3 && showGameFilter()">
            <div class="col-12">
              <base-input label="Event filtering">
                <el-select
                  v-model="gameFilterModeChart"
                  class="select-primary"
                  placeholder="Select filtering option"
                >
                  <el-option
                    class="select-primary"
                    label="All events"
                    value="all"
                  ></el-option>
                  <el-option
                    class="select-primary"
                    label="Filter by event"
                    value="filter"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <!-- Game dropdown - only shown when "Filter by game" is selected -->
            <div class="col-12" v-if="gameFilterModeChart === 'filter'">
              <base-input label="Select event">
                <el-select
                  v-model="widget.game_id"
                  class="select-primary"
                  placeholder="Select an event"
                  v-on:change="j++"
                >
                  <el-option
                    v-for="(game, index) in games"
                    class="select-primary"
                    :key="'GM' + index + j"
                    :label="game.name"
                    :value="game.id"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
          </div>

          <!-- Staff member filter - Statistics + Resources source (all/filter/breakdown) -->
          <div class="row mt-2" v-if="!isCompanyScope && widget.widget_type && (widget.widget_type.id === 3 || isResourceSource(widget.source)) && showCompanyUserFilter()">
            <div class="col-12">
              <base-input label="Staff member filtering">
                <el-select
                  v-model="companyUserFilterMode"
                  class="select-primary"
                  placeholder="Select filtering option"
                >
                  <el-option
                    class="select-primary"
                    label="All staff members"
                    value="all"
                  ></el-option>
                  <el-option
                    class="select-primary"
                    label="Filter by staff member"
                    value="filter"
                  ></el-option>
                  <el-option
                    class="select-primary"
                    label="Show breakdown by staff member"
                    value="breakdown"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <!-- Staff member dropdown - only shown when "Filter by staff member" is selected -->
            <div class="col-12" v-if="companyUserFilterMode === 'filter'">
              <base-input label="Select staff member">
                <el-select
                  v-model="widget.company_user_id"
                  class="select-primary"
                  placeholder="Select a staff member"
                  v-on:change="j++"
                >
                  <el-option
                    v-for="(user, index) in users"
                    class="select-primary"
                    :key="'CU' + index + j"
                    :label="getUserLabel(user)"
                    :value="user.id"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
          </div>

          <!-- Staff member filter - For Charts and Other Widgets (filter only, no breakdown) -->
          <div class="row mt-2" v-if="!isCompanyScope && widget.widget_type && widget.widget_type.id !== 2 && widget.widget_type.id !== 3 && !isResourceSource(widget.source) && showCompanyUserFilter()">
            <div class="col-12">
              <base-input label="Staff member filtering">
                <el-select
                  v-model="companyUserFilterMode"
                  class="select-primary"
                  placeholder="Select filtering option"
                >
                  <el-option
                    class="select-primary"
                    label="All staff members"
                    value="all"
                  ></el-option>
                  <el-option
                    class="select-primary"
                    label="Filter by staff member"
                    value="filter"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <!-- Staff member dropdown - only shown when "Filter by staff member" is selected -->
            <div class="col-12" v-if="companyUserFilterMode === 'filter'">
              <base-input label="Select staff member">
                <el-select
                  v-model="widget.company_user_id"
                  class="select-primary"
                  placeholder="Select a staff member"
                  v-on:change="j++"
                >
                  <el-option
                    v-for="(user, index) in users"
                    class="select-primary"
                    :key="'CU2' + index + j"
                    :label="getUserLabel(user)"
                    :value="user.id"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
          </div>

          <!-- Payment method filter - Payments data source -->
          <div class="row mt-2" v-if="widget.source && widget.source.id === 15">
            <div class="col">
              <el-switch active-color="#e14eca" v-on:input="j++"
                         v-model="widget.payment_all_methods" />
              <label> &nbsp; Include all payment methods</label>
            </div>
          </div>

          <div class="row mt-2" v-if="widget.source && widget.source.id === 15 && !widget.payment_all_methods">
            <div class="col">
              <base-input label="Payment methods to include">
                <el-select multiple clearable filterable
                           v-model="widget.payment_methods"
                           class="select-primary"
                           style="width: 100%"
                           placeholder="Select methods"
                           v-on:change="j++">
                  <el-option
                    v-for="(pm, index) in paymentMethodSelectOptions"
                    class="select-primary"
                    :key="'PM' + index"
                    :label="pm.label"
                    :value="pm.value"
                  ></el-option>
                </el-select>
              </base-input>
              <info-hint variant="primary" style="width: 100%">
                Only the selected methods are counted toward this widget. To see sales for the period without gift card redemptions inflating the total, select the methods you actually collect money through, such as <strong>card</strong> and <strong>cash</strong>, and leave gift card out.
              </info-hint>
            </div>
          </div>
          <!-- Cancelled transactions - Transactions source only -->
          <div class="row mt-2" v-if="widget.source && widget.source.id === 2">
            <div class="col">
              <div class="toggle-field">
                <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.include_cancelled_transactions" />
                <label class="text-white">Include cancelled transactions
                  <el-popover trigger="hover" placement="top" popper-class="otc-pop">
                    <div>Cancelled transactions are left out. Turn this on to count them, including any bookings, items or gift cards kept on them when they were cancelled.</div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
          </div>
          <!-- Promotion filter - Bookings, Transactions, Payments and Purchases -->
          <div class="row mt-2" v-if="showPromoFilter">
            <div class="col">
              <div class="toggle-field">
                <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.promo_all" />
                <label class="text-white">Include all promotions</label>
              </div>
            </div>
          </div>

          <div class="row mt-2" v-if="showPromoFilter && !widget.promo_all">
            <div class="col">
              <base-input label="Promotions to include">
                <el-select multiple clearable filterable
                           v-model="widget.promos"
                           class="select-primary"
                           style="width: 100%"
                           placeholder="Select promotions"
                           v-on:change="j++">
                  <el-option-group
                    v-for="group in promoOptionGroups"
                    :key="'PROMOG' + group.name"
                    :label="group.name">
                    <el-option
                      v-for="(p, index) in group.options"
                      class="select-primary"
                      :key="'PRM' + index"
                      :label="p.label"
                      :value="p.value"
                    ></el-option>
                  </el-option-group>
                </el-select>
              </base-input>
              <info-hint variant="primary" style="width: 100%">
                Only rows that used one of the selected promotions are counted toward this widget, so orders and bookings with no promotion drop out of the total. Selecting a promo code group covers every code in it.
              </info-hint>
            </div>
          </div>
          <!-- Email template filter - Emails source and Customer emails feed -->
          <div class="row mt-2" v-if="showEmailTemplateFilter">
            <div class="col">
              <div class="toggle-field">
                <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.email_template_all" />
                <label class="text-white">Include all email templates</label>
              </div>
            </div>
          </div>

          <div class="row mt-2" v-if="showEmailTemplateFilter && !widget.email_template_all">
            <div class="col">
              <base-input label="Email templates to include">
                <el-select multiple clearable filterable
                           v-model="widget.email_template_ids"
                           class="select-primary"
                           popper-class="otc-select-popper"
                           style="width: 100%"
                           placeholder="Select email templates"
                           v-on:change="j++">
                  <template v-for="group in emailTemplateOptionGroups">
                    <el-option-group v-if="group.name" :key="'ETG' + group.name" :label="group.name">
                      <el-option
                        v-for="t in group.options"
                        class="select-primary"
                        :key="'ET' + t.value"
                        :label="t.label"
                        :value="t.value"
                      >
                        <span>{{ t.label }}</span>
                        <span v-if="t.badge" class="badge-tag badge-tag--archived ml-2">{{ t.badge }}</span>
                      </el-option>
                    </el-option-group>
                    <template v-else>
                      <el-option
                        v-for="t in group.options"
                        class="select-primary"
                        :key="'ET' + t.value"
                        :label="t.label"
                        :value="t.value"
                      >
                        <span>{{ t.label }}</span>
                        <span v-if="t.badge" class="badge-tag badge-tag--archived ml-2">{{ t.badge }}</span>
                      </el-option>
                    </template>
                  </template>
                </el-select>
              </base-input>
              <info-hint variant="primary" style="width: 100%">
                Only emails sent from the selected templates are counted toward this widget. Simple emails such as booking confirmations have no template, so they drop out while this filter is active. Archived templates are listed so older emails can still be reported on.
              </info-hint>
            </div>
          </div>

          <div class="row mt-2" v-if="showEmailTemplateFilter">
            <div class="col">
              <div class="toggle-field">
                <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.email_template_type_all" />
                <label class="text-white">Include all template types</label>
              </div>
            </div>
          </div>

          <div class="row mt-2" v-if="showEmailTemplateFilter && !widget.email_template_type_all">
            <div class="col">
              <base-input label="Template types to include">
                <el-select multiple clearable filterable
                           v-model="widget.email_template_type_ids"
                           class="select-primary"
                           popper-class="otc-select-popper"
                           style="width: 100%"
                           placeholder="Select template types"
                           v-on:change="j++">
                  <template v-for="group in emailTemplateTypeOptionGroups">
                    <el-option-group v-if="group.name" :key="'ETTG' + group.name" :label="group.name">
                      <el-option
                        v-for="t in group.options"
                        class="select-primary"
                        :key="'ETT' + t.value"
                        :label="t.label"
                        :value="t.value"
                      ></el-option>
                    </el-option-group>
                    <template v-else>
                      <el-option
                        v-for="t in group.options"
                        class="select-primary"
                        :key="'ETT' + t.value"
                        :label="t.label"
                        :value="t.value"
                      ></el-option>
                    </template>
                  </template>
                </el-select>
              </base-input>
              <info-hint variant="primary" style="width: 100%">
                Only emails sent from templates of the selected types are counted toward this widget. Simple emails such as booking confirmations have no template type, so they drop out while this filter is active.
              </info-hint>
            </div>
          </div>

          <div class="row mt-2" v-if="showUtmFilter">
            <div class="col">
              <div class="toggle-field">
                <el-switch active-color="#e14eca" v-on:input="j++" v-model="widget.utm_all" />
                <label class="text-white">Include all traffic</label>
              </div>
            </div>
          </div>

          <div class="row mt-2" v-if="showUtmFilter && !widget.utm_all">
            <div class="col">
              <base-input label="Traffic sources to include">
                <el-select multiple clearable filterable allow-create default-first-option
                           v-model="widget.utm_sources"
                           class="select-primary"
                           popper-class="otc-select-popper"
                           style="width: 100%"
                           placeholder="Select or type sources"
                           v-on:change="j++">
                  <el-option v-for="o in utmSourceSelectOptions" class="select-primary" :key="'US' + o.value" :label="o.label" :value="o.value"></el-option>
                </el-select>
              </base-input>
              <base-input label="Traffic mediums to include">
                <el-select multiple clearable filterable allow-create default-first-option
                           v-model="widget.utm_mediums"
                           class="select-primary"
                           popper-class="otc-select-popper"
                           style="width: 100%"
                           placeholder="Select or type mediums"
                           v-on:change="j++">
                  <el-option v-for="o in utmMediumSelectOptions" class="select-primary" :key="'UM' + o.value" :label="o.label" :value="o.value"></el-option>
                </el-select>
              </base-input>
              <base-input label="Campaigns to include">
                <el-select multiple clearable filterable allow-create default-first-option
                           v-model="widget.utm_campaigns"
                           class="select-primary"
                           popper-class="otc-select-popper"
                           style="width: 100%"
                           placeholder="Select or type campaigns"
                           v-on:change="j++">
                  <el-option v-for="o in utmCampaignSelectOptions" class="select-primary" :key="'UC' + o.value" :label="o.label" :value="o.value"></el-option>
                </el-select>
              </base-input>
              <info-hint variant="primary" style="width: 100%">
                Only rows whose traffic attribution matches every selected dimension are counted. Values match regardless of capitalisation. Lists show values seen in the last twelve months; older values can be typed.
              </info-hint>
            </div>
          </div>
          <!-- Purchase type filter - Purchases data source -->
          <div class="row mt-2" v-if="widget.source && widget.source.id === 17">
            <div class="col">
              <label class="text-white">Select which purchase types to include:</label>
              <div class="mt-2">
                <base-checkbox v-model="widget.purchase_types_booking" class="mb-2">
                  Bookings
                </base-checkbox>
                <div v-if="widget.purchase_types_booking && !isCompanyScope" class="ml-4 mb-3">
                  <el-select multiple clearable filterable
                             v-model="widget.purchase_filter_game_ids"
                             class="select-primary"
                             style="width: 100%"
                             placeholder="All events"
                             v-on:change="j++">
                    <el-option
                      v-for="(game, index) in games"
                      class="select-primary"
                      :key="'PFG' + index"
                      :label="game.name"
                      :value="game.id"
                    ></el-option>
                  </el-select>
                </div>

                <base-checkbox v-model="widget.purchase_types_gift_card" class="mb-2">
                  Gift cards
                </base-checkbox>
                <div v-if="widget.purchase_types_gift_card && !isCompanyScope" class="ml-4 mb-3">
                  <el-select multiple clearable filterable
                             v-model="widget.purchase_filter_gift_card_type_ids"
                             class="select-primary"
                             style="width: 100%"
                             placeholder="All gift card types"
                             v-on:change="j++">
                    <el-option
                      v-for="(gct, index) in gift_card_types"
                      class="select-primary"
                      :key="'PFGCT' + index"
                      :label="gct.name"
                      :value="gct.id"
                    ></el-option>
                  </el-select>
                  <div class="mt-2 toggle-field">
                    <el-switch active-color="#e14eca" v-model="widget.include_inactive_gift_cards" />
                    <label>Include inactive gift cards</label>
                  </div>
                </div>

                <base-checkbox v-model="widget.purchase_types_merchandise" class="mb-2">
                  Merchandise
                </base-checkbox>
                <div v-if="widget.purchase_types_merchandise && !isCompanyScope" class="ml-4 mb-3">
                  <el-select multiple clearable filterable
                             v-model="widget.purchase_filter_item_ids"
                             class="select-primary"
                             style="width: 100%"
                             placeholder="All items"
                             v-on:change="j++">
                    <el-option
                      v-for="(item, index) in items"
                      class="select-primary"
                      :key="'PFI' + index"
                      :label="item.name"
                      :value="item.id"
                    ></el-option>
                  </el-select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </drawer-section>

      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" @click="modals.new_widget = false">Cancel</base-button>
          <base-button v-if="!widget.hasOwnProperty('id')" type="primary" size="sm" @click="createWidget()">Create</base-button>
          <base-button v-if="widget.hasOwnProperty('id')" type="primary" size="sm" @click="updateWidget(widget)">Update</base-button>
        </div>
      </template>
    </base-drawer>


    <copy-dashboard-drawer
      :show="showCopyDrawer"
      :dashboard="dashboard || null"
      :groups="allGroups"
      :source-group-id="sourceGroupId"
      :loading="copyLoading"
      @close="showCopyDrawer = false"
      @copy="copyToGroups"
    />

    <dashboard-settings-drawer
      :show="settingsDrawerOpen"
      :dashboard="dashboard || null"
      :users="users"
      :viewers="viewers"
      :editors="editors"
      :role-options="roleOptions"
      :allowed-roles="allowedRoles"
      :filter-config="filterBarConfig"
      :filter-options="filterDrawerOptions"
      :loading="settingsSaving"
      @close="settingsDrawerOpen = false"
      @save="saveDashboardSettings"
    />

    <base-alert-dialog
      :show="alertDialog.show"
      :title="alertDialog.title"
      :message="alertDialog.message"
      :variant="alertDialog.variant"
      @confirm="alertDialog.show = false"
      @cancel="alertDialog.show = false"
    />

  </div>
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import {BTable} from "bootstrap-vue";
import {Dropdown, DropdownMenu, Switch} from "element-ui";
import VueGridLayout from 'vue-grid-layout';
import { emailTemplateOptions, emailTemplateTypeOptions, utmValueOptions } from './dashboardGlobalFilters'
import { formatUtmValue } from '../../../util/utmAttribution'
import PieChart from "@/components/Charts/PieChart.vue";
import BarChart from "@/components/Charts/BarChart.vue";
import LineChart from "@/components/Charts/LineChart.vue";
import HeatmapChart from "@/components/Charts/HeatmapChart.vue";
import FunnelChart from "@/components/Charts/FunnelChart.vue";
import GaugeChart from "@/components/Charts/GaugeChart.vue";
import GroupComparisonChart from "@/components/Charts/GroupComparisonChart.vue";
import { shortenWidgetDescription } from "@/util/widgetDescription";
import { describeActivityEvent } from "@/util/activityStreamEvent";
import { viewportColumns, repackLayout } from "@/util/responsiveGridLayout";
import UtilizationWidget from "@/components/Charts/UtilizationWidget.vue";
import WidgetAiPrompt from "@/pages/Dashboard/Dashboards/components/WidgetAiPrompt.vue";
import CopyDashboardDrawer from "@/pages/Dashboard/Dashboards/CopyDashboardDrawer.vue";
import DashboardSettingsDrawer from "@/pages/Dashboard/Dashboards/DashboardSettingsDrawer.vue";
import { emptyConfig, normalizeConfig, buildFilterSchema, buildWidgetQuery, activeLabelParts, globalPeriodActive, FILTER_LABELS } from "@/pages/Dashboard/Dashboards/dashboardGlobalFilters";
import { allowedRolesFromRows } from "@/pages/Dashboard/Dashboards/dashboardAccess";

// Sources certified for the All groups (company) widget scope. Keep equal to
// COMPANY_SCOPE_SOURCE_IDS in the dashboard widget lambdas; the jest rules test pins the lambda copy.
const COMPANY_SCOPE_SOURCE_IDS = [1, 2, 12, 15, 17, 25, 26];
import {date, formatGroupDate, formatGroupTime} from "@/plugins/dates";
import moment from 'moment'
import _ from "lodash";
import currencies from "@/assets/currencies.json";
import html2canvas from 'html2canvas';

export default {
  name: "EditDashboard",
  components: {
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    BreadcrumbItem,
    Breadcrumb,
    BTable,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [Switch.name]: Switch,
    WidgetAiPrompt,
    CopyDashboardDrawer,
    DashboardSettingsDrawer,
    PieChart,
    BarChart,
    LineChart,
    HeatmapChart,
    FunnelChart,
    GaugeChart,
    GroupComparisonChart,
    UtilizationWidget,
  },
  data() {
    return {
      loading: false,
      layout: [],
      showCopyDrawer: false,
      settingsDrawerOpen: false,
      settingsSaving: false,
      filterBarConfig: emptyConfig(),
      filterOptions: null,
      filterValues: {},
      filterBarKey: 0,
      filtersLoading: false,
      alertDialog: { show: false, title: '', message: '', variant: 'success' },
      copyLoading: false,
      allGroups: [],
      widgetTemplates: [],
      widgetTemplatesLoading: false,
      layoutReady: false,
      viewportCols: 0,   // 0 = stored layout; 1 or 2 = repacked for narrow viewports (never persisted)
      selectedWidgetTemplateId: null,
      widgetLoadingStates: {}, // Track loading state per widget
      measurements: [{label:'count', name: 'Count'}, {label: 'amount', name: 'Amount paid'}, {label: 'refunded', name: 'Amount refunded'}],
      purchaseMeasurements: [{label:'count', name: 'Number of sales'}, {label: 'quantity', name: 'Units sold'}, {label: 'amount', name: 'Revenue (total)'}, {label: 'price', name: 'Subtotal'}, {label: 'tax', name: 'Tax'}, {label: 'fee', name: 'Fee'}, {label: 'discount', name: 'Discount'}],
      pmMeasurements: [{label:'count', name: 'Count'}],
      currentMeasurements: [{label:'count', name: 'Count'}, {label: 'amount', name: 'Amount paid'}, {label: 'refunded', name: 'Amount refunded'}],
      modals: { new_widget: false, export: false},
      // AI widget generation (Biku) is built + deployed but parked behind this flag while the
      // UX/approach is reconsidered. Flip to true to re-enable the header trigger + panel.
      aiWidgetGenerationEnabled: false,
      showAiAssistant: false,
      exportFormat: 'csv',
      exportWidget: null,
      dashboard: '',
      sources: [],
      widget_types: [],
      metric_types: [],
      all_metric_types: [],
      chart_types: [],
      fields: [],
      all_fields: [],
      customFieldOptions: [],
      customFieldsLoading: false,
      activity_sources: [],
      games: [],
      items: [],
      gift_card_types: [],
      widget: {
        widget_type: '',
        source: '',
        field: '',
        title: '',
        label: '',
        size: 2,
        position: 1,
        is_all_time: true,
        scope: 'group',
        company_group_ids: [],
        game_id: null,
        group_by_game: false,
        company_user_id: null,
        group_by_user: false,
        group_by_field: null,
        custom_field_id: null,
        show_description_label: true,
        allow_export: true,
        show_compare_option: true,
        default_granularity: 'auto',
        default_compare_mode: 'none',
        default_compare_count: 2,
        purchase_types_booking: true,
        purchase_types_gift_card: true,
        purchase_types_merchandise: true,
        purchase_filter_game_ids: [],
        purchase_filter_gift_card_type_ids: [],
        purchase_filter_item_ids: [],
        include_inactive_gift_cards: false,
        include_cancelled_transactions: false,
        payment_all_methods: true,
        payment_methods: [],
        promo_all: true,
        promos: [],
        email_template_all: true,
        email_template_ids: [],
        email_template_type_all: true,
        email_template_type_ids: [],
        utm_all: true,
        utm_sources: [],
        utm_mediums: [],
        utm_campaigns: [],
        utm_level: 'source'
      },
      paymentMethodOptions: [],
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week,
      day_of_week: '',
      date_format_cal: '',
      currency_symbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_format: JSON.parse(localStorage.getItem('group')).currency_format,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currencies: currencies.currencies,
      j: 0,
      errs: {
        widget_type: '',
        chart_type: '',
        data_source: '',
        field: '',
        activity_source: '',
        number_of_events: '',
        metric_type: '',
        custom_field: '',
        date_range: ''
      },
      time_period_types: [],
      users: [],
      editors: [],
      viewers: [],
      allowedRoles: [],
      roleOptions: [],
      rolesLoaded: false
    }
  },
  computed: {
    filterSchema(){
      if(!this.filterOptions) return []
      return buildFilterSchema(this.filterBarConfig.keys, this.filterValues, { ...this.filterOptions, timePeriodTypes: this.time_period_types, paymentMethods: this.paymentMethodOptions })
    },
    globalPeriodOn(){
      return this.filterBarConfig.enabled && globalPeriodActive(this.filterValues)
    },
    filterDrawerOptions(){
      if(!this.filterOptions) return null
      return { ...this.filterOptions, timePeriodTypes: this.time_period_types, paymentMethods: this.paymentMethodOptions }
    },
    sourceGroupId(){
      return JSON.parse(localStorage.getItem('group')).id
    },
    gridLayout: {
      // Narrow viewports get an in-memory repack; the stored layout only changes on desktop.
      get(){ return this.viewportCols === 0 ? this.layout : repackLayout(this.layout, this.viewportCols) },
      set(value){ if(this.viewportCols === 0) this.layout = value }
    },
    gridCols(){
      // Always 12: legacy 6-column dashboards are doubled in memory at load, so the grid never
      // renders at 6. A transient 6-column frame before the dashboard loaded made vue-grid-layout
      // clamp and compact 12-column layouts (and the settings page then persisted the damage).
      return 12
    },
    isCompanyScope(){
      return !!(this.widget && this.widget.scope === 'company')
    },
    scopeOptions(){
      return [{ value: 'group', label: 'This group' }, { value: 'company', label: 'All groups' }]
    },
    companyGroupOptions(){
      return [...this.allGroups].sort((a, b) => a.name.localeCompare(b.name))
    },
    groupByCurrency: {
      get(){ return !!(this.widget && this.widget.group_by_field === 'currency') },
      set(on){ this.$set(this.widget, 'group_by_field', on ? 'currency' : null) }
    },
    isUtmField(){
      // The traffic attribution field is registered per data source with field_name 'us.source'.
      if(!this.widget.field || !this.widget.field.id) return false
      if(this.widget.field.field_name) return this.widget.field.field_name === 'us.source'
      const row = (this.all_fields || []).find(f => f.id === this.widget.field.id)
      return !!(row && row.field_name === 'us.source')
    },
    showUtmLevel(){
      return !!(this.widget.widget_type && this.widget.widget_type.id === 1
        && this.widget.chart_type && [2, 3].includes(this.widget.chart_type.id) && this.isUtmField)
    },
    utmLevelOptions(){
      return [
        { value: 'source', label: 'Source' },
        { value: 'source_medium', label: 'Source and medium' },
        { value: 'campaign', label: 'Source, medium and campaign' },
      ]
    },
    showCurrencyGrouping(){
      return this.isCompanyScope && this.isMoneyField && !!this.widget.widget_type && this.widget.widget_type.id === 3
        && !!this.widget.source && [1, 2, 15, 17].includes(this.widget.source.id)
    },
    splitByGroup: {
      get(){ return !!(this.widget && this.widget.group_by_field === 'company_group') },
      set(on){
        this.$set(this.widget, 'group_by_field', on ? 'company_group' : null)
        this.updateFields()
      }
    },
    locationPresets(){
      // Country / state / city presets from the groups list, in that order, each with the ids it covers.
      const presets = []
      const levels = [['country', 'Country'], ['state', 'State'], ['city', 'City']]
      for(const [field, title] of levels){
        const byValue = new Map()
        for(const g of this.allGroups){
          const v = (g[field] || '').toString().trim()
          if(!v) continue
          if(!byValue.has(v)) byValue.set(v, [])
          byValue.get(v).push(g.id)
        }
        const entries = [...byValue.entries()].sort((a, b) => a[0].localeCompare(b[0]))
        entries.forEach(([value, ids], i) => presets.push({ key: field + ':' + value, label: title + ': ' + value, ids, divided: i === 0 && presets.length > 0 }))
      }
      return presets
    },
    isMoneyField(){
      return !!(this.widget && this.widget.field && this.widget.field.data_type === 'currency')
    },
    visibleWidgetTypes(){
      // Activity widgets are group-only.
      return this.widget_types.filter(wt => !this.isCompanyScope || (wt.id !== 2 && wt.id !== 4))
    },
    visibleSources(){
      // Sort a copy alphabetically by name (don't mutate the source array).
      // Custom fields are valid for both chart (distribution) and statistic (response count).
      // Tags is chart-only: both its fields are collection type, which the
      // statistic field list drops, so offering it there dead-ends on "No data".
      let sources = [...this.sources]
      if(this.widget && this.widget.widget_type && this.widget.widget_type.id === 3){
        sources = sources.filter(s => !this.isTagSource(s))
      }
      // Company scope: only the certified sources (mirrors the lambda allowlist).
      if(this.isCompanyScope){
        sources = sources.filter(s => COMPANY_SCOPE_SOURCE_IDS.includes(s.id))
      }
      return sources.sort((a, b) => a.name.localeCompare(b.name))
    },
    visibleChartTypes(){
      // Company scope: line, bar, and pie charts, plus the heatmap on the Bookings source (mirrors the lambda allowlist).
      if(this.isCompanyScope){
        return this.chart_types.filter(ct => [1, 2, 3].includes(ct.id)
          || (ct.id === 5 && this.widget.source && this.widget.source.id === 1)
          || (ct.id === 6 && this.widget.source && this.widget.source.id === 25)
          || (ct.id === 4 && this.widget.source && [1, 25].includes(this.widget.source.id)))
      }
      // Resources supports the gauge (snapshot) and line (trend over time). Bar/pie need a
      // category axis, which the numeric Resources fields aren't, so they're excluded.
      if(this.widget.source && this.isResourceSource(this.widget.source)){
        return this.chart_types.filter(ct => ct.id === 1 || ct.id === 4)
      }
      // Custom-field responses are a distribution — line charts make no sense, so
      // only offer pie/bar once that source is chosen.
      if(this.widget.source && this.isCustomFieldSource(this.widget.source)){
        return this.chart_types.filter(ct => ct.id === 2 || ct.id === 3)
      }
      // Tag breakdowns are distributions too — pie/bar only.
      if(this.widget.source && this.isTagSource(this.widget.source)){
        return this.chart_types.filter(ct => ct.id === 2 || ct.id === 3)
      }
      // The gauge (id 4) is for Resources, Bookings occupancy, and Carts conversion; the heatmap (id 5) is Bookings only; the funnel (id 6) Carts only.
      return this.chart_types.filter(ct => (ct.id !== 4 || (this.widget.source && [1, 25].includes(this.widget.source.id)))
        && (ct.id !== 5 || (this.widget.source && this.widget.source.id === 1))
        && (ct.id !== 6 || (this.widget.source && this.widget.source.id === 25)))
    },
    showPromoFilter(){
      // Mirrors promoFilterApplies in get-dashboard-widget-data: promotion-capable sources only,
      // and never the shapes that count unbooked slots (gauge, heatmap, funnel, occupancy).
      if(!this.widget.source) return false
      if(![1, 2, 15, 17].includes(this.widget.source.id)) return false
      if(this.widget.chart_type && [4, 5, 6].includes(this.widget.chart_type.id)) return false
      if(this.widget.field && this.widget.field.id === 117) return false
      return true
    },
    showUtmFilter(){
      // Mirrors utmFilterApplies in get-dashboard-widget-data: sources whose rows carry a utm id,
      // never the shapes that count unbooked slots (gauge, heatmap, funnel, occupancy).
      if(!this.widget.source) return false
      if(![1, 2, 4, 15, 17, 25].includes(this.widget.source.id)) return false
      if(this.widget.chart_type && [4, 5, 6].includes(this.widget.chart_type.id)) return false
      if(this.widget.field && this.widget.field.id === 117) return false
      return true
    },
    utmSourceSelectOptions(){ return this.utmSelectOptions('sources', this.widget.utm_sources) },
    utmMediumSelectOptions(){ return this.utmSelectOptions('mediums', this.widget.utm_mediums) },
    utmCampaignSelectOptions(){ return this.utmSelectOptions('campaigns', this.widget.utm_campaigns) },
    showEmailTemplateFilter(){
      // Mirrors emailTemplateFilterApplies in get-dashboard-widget-data: Emails source widgets and
      // the Customer emails activity feed.
      if(this.widget.widget_type && this.widget.widget_type.id === 2){
        return !!(this.widget.activity_source && this.widget.activity_source.id === 4)
      }
      return !!(this.widget.source && this.widget.source.id === 26)
    },
    emailTemplateSelectOptions(){
      const options = emailTemplateOptions(this.filterOptions || {})
      const known = new Set(options.map(o => o.value))
      if(Array.isArray(this.widget.email_template_ids)){
        this.widget.email_template_ids.forEach(id => {
          if(id && !known.has(id)){ options.push({ value: id, label: 'Template ' + id }); known.add(id) }
        })
      }
      return options
    },
    emailTemplateTypeSelectOptions(){
      const options = emailTemplateTypeOptions(this.filterOptions || {})
      const known = new Set(options.map(o => o.value))
      if(Array.isArray(this.widget.email_template_type_ids)){
        this.widget.email_template_type_ids.forEach(id => {
          if(id && !known.has(id)){ options.push({ value: id, label: 'Template type ' + id }); known.add(id) }
        })
      }
      return options
    },
    emailTemplateOptionGroups(){ return this.groupOptions(this.emailTemplateSelectOptions) },
    emailTemplateTypeOptionGroups(){ return this.groupOptions(this.emailTemplateTypeSelectOptions) },
    promoSelectOptions(){
      // From filter_options on the dashboard payload. Promo code groups first, then codes, each with its
      // group header. Any value already stored on the widget that is no longer in the list is
      // appended so the chip shows something readable instead of the raw reference.
      const options = this.filterOptions && Array.isArray(this.filterOptions.promos) ? [...this.filterOptions.promos] : []
      const known = new Set(options.map(o => o.value))
      if(Array.isArray(this.widget.promos)){
        this.widget.promos.forEach(value => {
          if(value && !known.has(value)){
            options.push({ value, label: String(value).replace('campaign:', 'Promo code group ').replace('code:', 'Code '), group: 'Codes' })
            known.add(value)
          }
        })
      }
      return options
    },
    promoOptionGroups(){
      const groups = []
      for(const o of this.promoSelectOptions){
        const name = o.group || 'Codes'
        let g = groups.find(x => x.name === name)
        if(!g){ g = { name, options: [] }; groups.push(g) }
        g.options.push(o)
      }
      return groups
    },
    paymentMethodSelectOptions(){
      // Configured methods plus any value already saved on the widget that is no
      // longer in the group's config (deleted/legacy method). Without this the
      // multi-select would show the raw stored code as the chip label.
      const options = [...this.paymentMethodOptions]
      const known = new Set(options.map(o => o.value))
      if(Array.isArray(this.widget.payment_methods)){
        this.widget.payment_methods.forEach(value => {
          if(value && !known.has(value)){
            options.push({ value, label: this.formatMethodLabel(value) })
            known.add(value)
          }
        })
      }
      return options
    },
    gameFilterMode: {
      get() {
        // Ensure game_id and group_by_game have proper defaults
        if (!this.widget) return 'all';

        if (this.widget.group_by_game === true) {
          return 'breakdown';
        } else if (this.widget.game_id !== null && this.widget.game_id !== undefined) {
          return 'filter';
        }
        // Default to 'all' - all games, no filtering
        return 'all';
      },
      set(value) {
        if (value === 'all') {
          this.$set(this.widget, 'game_id', null);
          this.$set(this.widget, 'group_by_game', false);
        } else if (value === 'filter') {
          this.$set(this.widget, 'group_by_game', false);
          // Set to first game if no game selected, so getter returns 'filter'
          if (!this.widget.game_id && this.games.length > 0) {
            this.$set(this.widget, 'game_id', this.games[0].id);
          }
        } else if (value === 'breakdown') {
          this.$set(this.widget, 'game_id', null);
          this.$set(this.widget, 'group_by_game', true);
        }
        this.j++;
      }
    },
    gameFilterModeChart: {
      get() {
        // For charts - only 'all' or 'filter' (no breakdown)
        if (!this.widget) return 'all';

        if (this.widget.game_id !== null && this.widget.game_id !== undefined) {
          return 'filter';
        }
        // Default to 'all' - all games, no filtering
        return 'all';
      },
      set(value) {
        if (value === 'all') {
          this.$set(this.widget, 'game_id', null);
        } else if (value === 'filter') {
          // Set to first game if no game selected, so getter returns 'filter'
          if (!this.widget.game_id && this.games.length > 0) {
            this.$set(this.widget, 'game_id', this.games[0].id);
          }
        }
        this.j++;
      }
    },
    companyUserFilterMode: {
      get() {
        if (!this.widget) return 'all';
        if (this.widget.group_by_user === true) {
          return 'breakdown';
        } else if (this.widget.company_user_id !== null && this.widget.company_user_id !== undefined) {
          return 'filter';
        }
        return 'all';
      },
      set(value) {
        if (value === 'all') {
          this.$set(this.widget, 'company_user_id', null);
          this.$set(this.widget, 'group_by_user', false);
        } else if (value === 'filter') {
          this.$set(this.widget, 'group_by_user', false);
          if (!this.widget.company_user_id && this.users.length > 0) {
            this.$set(this.widget, 'company_user_id', this.users[0].id);
          }
        } else if (value === 'breakdown') {
          this.$set(this.widget, 'company_user_id', null);
          this.$set(this.widget, 'group_by_user', true);
        }
        this.j++;
      }
    }
  },
  watch: {
    'modals.new_widget'(newVal) {
      // BaseDrawer doesn't handle ESC; add/remove a keydown listener while it's open.
      if (newVal) {
        document.addEventListener('keydown', this.onWidgetDrawerEsc);
      } else {
        document.removeEventListener('keydown', this.onWidgetDrawerEsc);
      }
    },
    'widget.source': {
      handler(newSource) {
        // Update measurement options based on data source
        if (newSource && newSource.id === 17) {
          this.currentMeasurements = [...this.purchaseMeasurements];
        } else if (newSource && (newSource.id === 18 || newSource.id === 19)) {
          this.currentMeasurements = [...this.pmMeasurements];
        } else {
          this.currentMeasurements = [...this.measurements];
        }
        this.j++;
      },
      deep: true
    },
    showCurrencyGrouping(on) {
      // The toggle only exists for a company-scope money statistic on a supported source. When a
      // field, source, type or scope change takes that away, drop the stored value so a save never
      // sends group_by_field = 'currency' the lambdas would reject. Runs after the tick, so the
      // edit-drawer cascade (field set after updateFields) never trips it.
      if(!on && this.widget && this.widget.group_by_field === 'currency'){
        this.$set(this.widget, 'group_by_field', null)
      }
    },
    'widget.field'(field) {
      // Switching onto a money field with a mixed-currency selection: clear it, the picker
      // can only combine groups that share one currency.
      if(!field || field.data_type !== 'currency') return
      if(this.groupByCurrency) return
      const ids = this.widget.company_group_ids
      if(this.widget.scope !== 'company' || !Array.isArray(ids) || ids.length < 2) return
      // Judge only ids that resolve against allGroups: an id missing from the groups
      // response (or a not-yet-loaded list) is not evidence of mixed currencies, and
      // treating it as one silently wiped valid selections on drawer open.
      const currencies = new Set(ids
        .map(id => { const g = this.allGroups.find(x => x.id === id); return g ? g.currency : null })
        .filter(c => c !== null && c !== undefined))
      if(currencies.size > 1){
        this.$set(this.widget, 'company_group_ids', [])
        this.$notify({ type: 'warning', message: 'Group selection cleared: money metrics need groups that share one currency' })
      }
    },
    'widget.purchase_types_booking'(newVal) {
      if (!newVal && this.widget && typeof this.widget === 'object') {
        this.$set(this.widget, 'purchase_filter_game_ids', [])
      }
    },
    'widget.purchase_types_gift_card'(newVal) {
      if (!newVal && this.widget && typeof this.widget === 'object') {
        this.$set(this.widget, 'purchase_filter_gift_card_type_ids', [])
      }
    },
    'widget.purchase_types_merchandise'(newVal) {
      if (!newVal && this.widget && typeof this.widget === 'object') {
        this.$set(this.widget, 'purchase_filter_item_ids', [])
      }
    }
  },
  methods:{
    showGameFilter(){
      // Show game filter for data sources that relate to bookings/games
      if (!this.widget.source) return false
      // Table IDs: 1=bookings, 3=games, 13=waivers (all relate to games)
      return [1, 3, 13].includes(this.widget.source.id)
    },
    showCompanyUserFilter(){
      if (!this.widget.source) return false
      // Resources source always supports staff filtering / breakdown (resources are staffed).
      if (this.isResourceSource(this.widget.source)) return true
      // Table IDs: 3=games, 8=checklists (both have game_master_id field)
      return [3, 8].includes(this.widget.source.id)
    },
    onWidgetDrawerEsc(e){
      if (e.key === 'Escape') {
        this.modals.new_widget = false
      }
    },
    isCustomFieldSource(source){
      // Keyed on name (not a hardcoded id) so it matches whatever id the migration assigned.
      return !!source && source.name === 'Custom fields'
    },
    isResourceSource(source){
      // Resources source (OTC-2506): utilization of staffed resources. Keyed on name.
      return !!source && source.name === 'Resources'
    },
    isTagSource(source){
      // Tags source (fields: booking tags / customer tags). Keyed on name.
      return !!source && source.name === 'Tags'
    },
    onScopeChange(next){
      const scope = next === 'company' ? 'company' : 'group'
      if(this.widget.scope === scope) return
      this.$set(this.widget, 'scope', scope)
      if(scope === 'company'){
        // Reset everything company scope cannot carry, then rerun the field cascade.
        this.$set(this.widget, 'source', '')
        this.$set(this.widget, 'field', {})
        this.$set(this.widget, 'metric_type', {})
        this.$set(this.widget, 'game_id', null)
        this.$set(this.widget, 'company_user_id', null)
        this.$set(this.widget, 'custom_field_id', null)
        this.$set(this.widget, 'group_by_game', false)
        this.$set(this.widget, 'group_by_user', false)
        this.$set(this.widget, 'group_by_field', null)
        this.$set(this.widget, 'purchase_filter_game_ids', [])
        this.$set(this.widget, 'purchase_filter_gift_card_type_ids', [])
        this.$set(this.widget, 'purchase_filter_item_ids', [])
        this.$set(this.widget, 'is_all_time', false)
        this.$set(this.widget, 'company_group_ids', [])
        if(this.widget.chart_type && this.widget.chart_type.id !== 1){
          const line = _.find(this.chart_types, {id: 1})
          this.$set(this.widget, 'chart_type', line ? {id: line.id, name: line.name} : '')
        }
        this.updateFields()
        if(this.allGroups.length === 0){
          this.getGroups()
        }
      } else {
        this.$set(this.widget, 'company_group_ids', [])
        if(this.widget.group_by_field === 'company_group' || this.widget.group_by_field === 'currency'){
          this.$set(this.widget, 'group_by_field', null)
          this.updateFields()
        }
      }
      this.j++
    },
    isCurrencyScoped(item){
      // A company-scope money widget pinned to a same-currency subset while other groups
      // use different currencies (the auto-scoped template case, or a manual selection).
      if(!item || item.scope !== 'company' || item.data_type !== 'currency') return false
      if(item.group_by_field === 'currency') return false
      const ids = item.company_group_ids
      if(!Array.isArray(ids) || ids.length === 0 || this.allGroups.length === 0) return false
      const selected = new Set(ids)
      const currencies = new Set(this.allGroups.filter(g => selected.has(g.id)).map(g => g.currency))
      if(currencies.size !== 1) return false
      const currency = [...currencies][0]
      return this.allGroups.some(g => !selected.has(g.id) && g.currency !== currency)
    },
    currencyScopedNote(item){
      const n = Array.isArray(item.company_group_ids) ? item.company_group_ids.length : 0
      return 'Limited to ' + n + (n === 1 ? ' group' : ' groups') + ' sharing one currency because your groups use different currencies. Edit the widget to change the selection.'
    },
    applyLocationPreset(preset){
      // Money fields can only mix one currency: keep the preset's groups that match the first one.
      let ids = [...preset.ids]
      if(this.isMoneyField && !this.groupByCurrency){
        const first = this.allGroups.find(x => x.id === ids[0])
        const before = ids.length
        ids = ids.filter(id => { const g = this.allGroups.find(x => x.id === id); return g && first && g.currency === first.currency })
        if(ids.length < before){
          this.$notify({ type: 'warning', message: (before - ids.length) + ' groups in ' + preset.label + ' use a different currency and were left out' })
        }
      }
      this.$set(this.widget, 'company_group_ids', ids)
      this.j++
    },
    selectAllGroups(){
      // Money fields can only mix one currency: keep the groups that share the current group's
      // currency (falling back to the first group when the current one is not in the list).
      let ids = this.allGroups.map(g => g.id)
      if(this.isMoneyField && !this.groupByCurrency){
        const currentId = JSON.parse(localStorage.getItem('group')).id
        const anchor = this.allGroups.find(g => g.id === currentId) || this.allGroups[0]
        const before = ids.length
        ids = this.allGroups.filter(g => g.currency === anchor.currency).map(g => g.id)
        if(ids.length < before){
          this.$notify({ type: 'warning', message: (before - ids.length) + ' groups use a different currency than ' + anchor.currency + ' and were left out' })
        }
      }
      this.$set(this.widget, 'company_group_ids', ids)
      this.j++
    },
    isGroupDisabledForCurrency(g){
      if(!this.isMoneyField || this.groupByCurrency || !Array.isArray(this.widget.company_group_ids) || this.widget.company_group_ids.length === 0) return false
      const first = this.allGroups.find(x => x.id === this.widget.company_group_ids[0])
      return !!first && first.currency !== g.currency
    },
    async fetchCustomFieldsForStats(){
      this.customFieldsLoading = true
      try {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/custom-fields?count=500&archived=0'
        let response = await this.axios.get(url, config)
        const fields = response.data.custom_fields || []
        // Only dropdown (3) and checkbox (1) fields have bounded values that aggregate into a clean distribution.
        const usable = fields.filter(f => f.type === 3 || f.type === 1)
        // The same question often exists as many custom_fields rows (one per booking site).
        // The widget aggregates responses by label, so dedupe the picker to one entry per
        // label — the stored id is just a representative; the backend resolves its label.
        const seen = new Set()
        this.customFieldOptions = usable.filter(f => {
          const key = (f.label || '').trim().toLowerCase()
          if(seen.has(key)) return false
          seen.add(key)
          return true
        })
        // Edit flow: the saved id may be a non-representative duplicate of the same label.
        // Repoint it at the kept representative so the picker shows the selection (backend
        // aggregates by label, so the swap is functionally a no-op).
        if(this.widget.custom_field_id){
          const stored = usable.find(f => f.id === this.widget.custom_field_id)
          if(stored){
            const rep = this.customFieldOptions.find(f => (f.label || '').trim().toLowerCase() === (stored.label || '').trim().toLowerCase())
            if(rep && rep.id !== this.widget.custom_field_id){
              this.$set(this.widget, 'custom_field_id', rep.id)
            }
          }
        }
      } catch(err) {
        console.log(err)
        this.customFieldOptions = []
      } finally {
        this.customFieldsLoading = false
        this.j++
      }
    },
    selectWidgetType(wt){
      const changed = !this.widget.widget_type || this.widget.widget_type.id !== wt.id
      this.$set(this.widget, 'widget_type', { id: wt.id, name: wt.name })
      if(wt.id === 4){ this.$set(this.widget, 'scope', 'group'); this.$set(this.widget, 'company_group_ids', []) }
      // Switching type clears config that doesn't carry across types, so a stale source/field/
      // metric/etc. can't leak into the UI or the saved payload (gate each type to what fits).
      if(changed){
        this.$set(this.widget, 'source', '')
        this.$set(this.widget, 'field', '')
        this.$set(this.widget, 'custom_field_id', null)
        this.$set(this.widget, 'metric_type', '')
        this.$set(this.widget, 'chart_type', '')
        this.$set(this.widget, 'activity_source', '')
        this.$set(this.widget, 'event_limit', null)
      }
      this.updateFields()
    },
    selectChartType(ct){
      this.$set(this.widget, 'chart_type', { id: ct.id, name: ct.name })
      this.updateFields()
    },
    widgetTypeIcon(id){
      return { 1: 'icon-chart-bar-32', 2: 'icon-bullet-list-67', 3: 'icon-single-copy-04', 4: 'icon-align-left-2' }[id] || 'icon-app'
    },
    chartTypeIcon(id){
      // 1 = line, 2 = bar, 3 = pie, 4 = gauge
      return { 1: 'icon-vector', 2: 'icon-chart-bar-32', 3: 'icon-chart-pie-36', 4: 'icon-sound-wave', 5: 'icon-components', 6: 'icon-minimal-down' }[id] || 'icon-app'
    },
    showTimeRangeSection(){
      // Only render the Time range section when it actually has a visible control:
      // the "show all records" toggle (statistic or non-line chart) OR a chosen range.
      if (!this.widget.widget_type || this.widget.widget_type.id === 2 || this.widget.widget_type.id === 4) return false
      const hasAllRecordsToggle = this.widget.widget_type.id === 3
        || (this.widget.chart_type && this.widget.chart_type.id !== 1)
      return hasAllRecordsToggle || !this.widget.is_all_time
    },
    showPmBreakdown(){
      // Statistic breakdown dimension for PM sources (18=work tickets, 19=time logs).
      // Only on statistic widgets — the breakdown control itself is stat-only, so gating
      // here also keeps the Filtering section from rendering empty for PM charts.
      if (!this.widget.source || !this.widget.widget_type) return false
      return [18, 19].includes(this.widget.source.id) && this.widget.widget_type.id === 3
    },
    pmBreakdownOptions(){
      if (!this.widget.source) return []
      if (this.widget.source.id === 18) {
        return [
          {label: 'Status', value: 'status'},
          {label: 'Priority', value: 'priority'},
          {label: 'Assignee', value: 'assignee'},
        ]
      }
      if (this.widget.source.id === 19) {
        return [
          {label: 'Category', value: 'category'},
          {label: 'User', value: 'user'},
          {label: 'Project', value: 'project'},
        ]
      }
      return []
    },
    showMeasurement(){
      return this.widget.widget_type && this.widget.widget_type.id === 1
      && this.widget.source && (this.widget.source.id === 15 || this.widget.source.id === 17)
      && this.widget.chart_type && this.widget.chart_type.id !== 1
    },
    getPurchaseTypesString(){
      // Build comma-separated string of selected purchase types (for MySQL SET column)
      const types = [];
      if(this.widget.purchase_types_booking) types.push('booking');
      if(this.widget.purchase_types_gift_card) types.push('gift_card');
      if(this.widget.purchase_types_merchandise) types.push('merchandise');
      return types.length > 0 ? types.join(',') : null;
    },
    setPurchaseTypesFromString(purchaseTypesString){
      // Set checkbox values from comma-separated string (for editWidget)
      const types = purchaseTypesString ? purchaseTypesString.split(',') : ['booking', 'gift_card', 'merchandise'];
      this.$set(this.widget, 'purchase_types_booking', types.includes('booking'));
      this.$set(this.widget, 'purchase_types_gift_card', types.includes('gift_card'));
      this.$set(this.widget, 'purchase_types_merchandise', types.includes('merchandise'));
    },
    getPurchaseItemFilters(){
      const filters = {}
      if (this.widget.purchase_filter_game_ids && this.widget.purchase_filter_game_ids.length > 0) {
        filters.game_ids = this.widget.purchase_filter_game_ids
      }
      if (this.widget.purchase_filter_gift_card_type_ids && this.widget.purchase_filter_gift_card_type_ids.length > 0) {
        filters.gift_card_type_ids = this.widget.purchase_filter_gift_card_type_ids
      }
      if (this.widget.purchase_filter_item_ids && this.widget.purchase_filter_item_ids.length > 0) {
        filters.item_ids = this.widget.purchase_filter_item_ids
      }
      if (this.widget.include_inactive_gift_cards) {
        filters.include_inactive_gift_cards = true
      }
      if (this.widget.include_cancelled_transactions) {
        filters.include_cancelled_transactions = true
      }
      if (!this.widget.payment_all_methods && this.widget.payment_methods && this.widget.payment_methods.length > 0) {
        filters.payment_types = this.widget.payment_methods
      }
      if (!this.widget.promo_all && this.widget.promos && this.widget.promos.length > 0) {
        filters.promos = this.widget.promos
      }
      if (!this.widget.email_template_all && this.widget.email_template_ids && this.widget.email_template_ids.length > 0) {
        filters.email_template_ids = this.widget.email_template_ids
      }
      if (!this.widget.email_template_type_all && this.widget.email_template_type_ids && this.widget.email_template_type_ids.length > 0) {
        filters.email_template_type_ids = this.widget.email_template_type_ids
      }
      if (this.isUtmField && ['source_medium', 'campaign'].includes(this.widget.utm_level)) {
        filters.utm_level = this.widget.utm_level
      }
      const utmClean = (arr) => Array.isArray(arr) ? [...new Set(arr.map(s => String(s).trim().toLowerCase()).filter(Boolean))] : []
      if (!this.widget.utm_all) {
        const s = utmClean(this.widget.utm_sources), m = utmClean(this.widget.utm_mediums), c = utmClean(this.widget.utm_campaigns)
        if (s.length > 0) filters.utm_sources = s
        if (m.length > 0) filters.utm_mediums = m
        if (c.length > 0) filters.utm_campaigns = c
      }
      return Object.keys(filters).length > 0 ? JSON.stringify(filters) : null
    },
    setPurchaseItemFiltersFromJson(filtersJson){
      let filters = null
      if (filtersJson) {
        try {
          filters = typeof filtersJson === 'string' ? JSON.parse(filtersJson) : filtersJson
        } catch (e) {
          console.error('Error parsing purchase_item_filters:', e)
        }
      }
      this.$set(this.widget, 'purchase_filter_game_ids', filters && filters.game_ids ? filters.game_ids : [])
      this.$set(this.widget, 'purchase_filter_gift_card_type_ids', filters && filters.gift_card_type_ids ? filters.gift_card_type_ids : [])
      this.$set(this.widget, 'purchase_filter_item_ids', filters && filters.item_ids ? filters.item_ids : [])
      this.$set(this.widget, 'include_inactive_gift_cards', filters && filters.include_inactive_gift_cards ? true : false)
      this.$set(this.widget, 'include_cancelled_transactions', filters && filters.include_cancelled_transactions ? true : false)
      const hasPaymentTypes = filters && Array.isArray(filters.payment_types) && filters.payment_types.length > 0
      this.$set(this.widget, 'payment_all_methods', !hasPaymentTypes)
      this.$set(this.widget, 'payment_methods', hasPaymentTypes ? filters.payment_types : [])
      const hasPromos = filters && Array.isArray(filters.promos) && filters.promos.length > 0
      this.$set(this.widget, 'promo_all', !hasPromos)
      this.$set(this.widget, 'promos', hasPromos ? filters.promos : [])
      const hasEmailTemplates = filters && Array.isArray(filters.email_template_ids) && filters.email_template_ids.length > 0
      this.$set(this.widget, 'email_template_all', !hasEmailTemplates)
      this.$set(this.widget, 'email_template_ids', hasEmailTemplates ? filters.email_template_ids.map(Number) : [])
      const hasEmailTemplateTypes = filters && Array.isArray(filters.email_template_type_ids) && filters.email_template_type_ids.length > 0
      this.$set(this.widget, 'email_template_type_all', !hasEmailTemplateTypes)
      this.$set(this.widget, 'email_template_type_ids', hasEmailTemplateTypes ? filters.email_template_type_ids.map(Number) : [])
      const utmArr = (k) => filters && Array.isArray(filters[k]) ? filters[k].map(String) : []
      const hasUtm = utmArr('utm_sources').length > 0 || utmArr('utm_mediums').length > 0 || utmArr('utm_campaigns').length > 0
      this.$set(this.widget, 'utm_all', !hasUtm)
      this.$set(this.widget, 'utm_sources', utmArr('utm_sources'))
      this.$set(this.widget, 'utm_mediums', utmArr('utm_mediums'))
      this.$set(this.widget, 'utm_campaigns', utmArr('utm_campaigns'))
      this.$set(this.widget, 'utm_level', filters && ['source_medium', 'campaign'].includes(filters.utm_level) ? filters.utm_level : 'source')
    },
    utmSelectOptions(dim, selected){
      // Values stored on the widget that fell out of the twelve-month option list are appended so the
      // chip still reads as a value instead of vanishing.
      const options = utmValueOptions(this.filterOptions || {}, dim)
      const known = new Set(options.map(o => o.value))
      if(Array.isArray(selected)){
        selected.forEach(v => { if(v && !known.has(v)){ options.push({ value: v, label: formatUtmValue(v) }); known.add(v) } })
      }
      return options
    },
    groupOptions(options){
      // Options with a group render under an el-option-group header; options without one
      // (single-group companies) share the unnamed group and render flat, so no empty header shows.
      const groups = []
      for(const o of options){
        const name = o.group || ''
        let g = groups.find(x => x.name === name)
        if(!g){ g = { name, options: [] }; groups.push(g) }
        g.options.push(o)
      }
      return groups
    },
    getUserLabel(user){
      if(user.first_name && !user.last_name){
        return user.first_name
      } else if(!user.first_name && user.last_name){
        return user.last_name
      } else if(user.first_name && user.last_name){
        return user.first_name + ' ' + user.last_name
      }
      return user.email
    },
    getLocalDateTime(d){
      if(!d) return ''
      const date = formatGroupDate(d, { dayOfWeek: false })
      const time = formatGroupTime(d)
      if(!date && !time) return ''
      return `${date}, ${time}`
    },
    async loadAllWidgets(){
      // Section widgets (type 4) have no data. Up to 8 requests run at once; a widget is only
      // dequeued while the dashboard captured up front is still the one on the route, so after a
      // duplicate moves the route to the copy nothing queued here is requested under its id (a 401).
      const dashboardId = this.$route.query.id
      const queue = this.layout.filter(w => w.widget_type_id !== 4)
      const worker = async () => {
        while (queue.length > 0) {
          if(this.$route.query.id !== dashboardId) return
          const w = queue.shift()
          await this.loadWidgetData(w, false)
        }
      }
      await Promise.all(Array.from({ length: Math.min(8, queue.length) }, worker))
    },
    async onGlobalFilterChange(values){
      this.filterValues = values
      this.filtersLoading = true
      await this.loadAllWidgets()
      this.filtersLoading = false
    },
    async onGlobalFilterQuickAction(key){
      if(key !== 'reset') return
      this.filterValues = JSON.parse(JSON.stringify(this.filterBarConfig.defaults))
      this.filterBarKey++
      await this.onGlobalFilterChange(this.filterValues)
    },
    globalFilterLine(item){
      if(!this.filterBarConfig.enabled || item.widget_type_id === 4) return []
      const parts = activeLabelParts(this.filterValues, this.filterBarConfig.keys, this.filterSchema)
      const state = item.filter_state || {}
      const stateKey = { period: 'date', location: 'location', vertical: 'event', genre: 'event', event_name: 'event', event: 'event', staff: 'staff', payment_method: 'payment_method', purchase_type: 'purchase_type', promo: 'promo', email_template: 'email_template', email_template_type: 'email_template_type', traffic: 'traffic' }
      return parts.map(p => {
        const st = state[stateKey[p.key]] || 'applied'
        const why = st === 'all_time' ? 'This widget shows all time and ignores the global date'
          : st === 'not_applicable' ? 'This widget\'s data source has no ' + FILTER_LABELS[p.key].toLowerCase() + ' dimension'
          : st === 'excluded' ? 'This widget is scoped to this group, which is not among the selected locations'
          : st === 'currency_conflict' ? 'The selected locations use different currencies, so this money widget cannot combine them'
          : ''
        return { key: p.key, label: p.label, applied: st === 'applied', why }
      })
    },
    unappliedGlobalFilters(item){
      return this.globalFilterLine(item).filter(f => !f.applied)
    },
    noDataText(item){
      const st = item.filter_state || {}
      if(st.location === 'excluded') return 'No data for the selected locations'
      if(st.location === 'currency_conflict') return 'Selected locations use different currencies'
      if(this.globalFilterLine(item).length > 0) return 'No data for the selected filters'
      return 'No data available'
    },
    goToDashboardSettings(){
      this.$router.push('/dashboards/settings')
    },
    addSectionPrompt(){
      // Reset widget to initial state with all properties for proper reactivity
      this.widget = {
        widget_type: '',
        source: '',
        field: '',
        title: '',
        label: '',
        size: 2,
        position: 1,
        is_all_time: true,
        scope: 'group',
        company_group_ids: [],
        y_axis_dimension: {label: 'count'},
        game_id: null,
        group_by_game: false,
        company_user_id: null,
        group_by_user: false,
        group_by_field: null,
        custom_field_id: null,
        show_description_label: true,
        allow_export: true,
        show_compare_option: true,
        default_granularity: 'auto',
        default_compare_mode: 'none',
        default_compare_count: 2,
        purchase_types_booking: true,
        purchase_types_gift_card: true,
        purchase_types_merchandise: true,
        purchase_filter_game_ids: [],
        purchase_filter_gift_card_type_ids: [],
        purchase_filter_item_ids: [],
        include_inactive_gift_cards: false,
        include_cancelled_transactions: false,
        payment_all_methods: true,
        payment_methods: [],
        promo_all: true,
        promos: [],
        email_template_all: true,
        email_template_ids: [],
        email_template_type_all: true,
        email_template_type_ids: [],
        utm_all: true,
        utm_sources: [],
        utm_mediums: [],
        utm_campaigns: [],
        utm_level: 'source'
      }
      // Reset measurement options to default (Payments)
      this.currentMeasurements = [...this.measurements];
      this.selectedWidgetTemplateId = null
      this.showAiAssistant = false
      if(this.widgetTemplates.length === 0){
        this.fetchWidgetTemplates()
      }
      this.modals.new_widget = true
    },
    selectBlankWidget(){
      // Back to an empty form; addSectionPrompt already holds the canonical reset.
      const reopen = this.modals.new_widget
      this.addSectionPrompt()
      this.modals.new_widget = reopen
    },
    async fetchWidgetTemplates(){
      this.widgetTemplatesLoading = true
      try {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let url = API_LOCATION + 'companies/' + companyId + '/dashboard-templates'
        let response = await this.axios.get(url, config)
        this.widgetTemplates = response.data.widget_templates || []
      } catch (err) {
        console.log(err)
      } finally {
        this.widgetTemplatesLoading = false
      }
    },
    applyWidgetTemplate(t){
      // Fresh form first so templates never stack on each other's leftovers.
      this.selectBlankWidget()
      this.selectedWidgetTemplateId = t.id
      const config = typeof t.config === 'string' ? JSON.parse(t.config) : t.config
      this.applyConfigToForm(config)
    },
    shortenWidgetDescription,
    nextFreeRow(){
      // First row below every existing tile, so a new widget can never overlap one.
      return this.layout.reduce((max, it) => Math.max(max, (Number(it.y) || 0) + (Number(it.h) || 0)), 0)
    },
    onWidgetResized(){
      // Tell fill-mode charts to re-read their container; chart.js's own container
      // detection is unreliable inside transformed grid items.
      window.dispatchEvent(new Event('dashboard-widget-resize'))
    },
    setColor(item, color){
      item.highlight_color = color
      this.updateWidget(item, 'highlight_color')
    },
    getAmount(data_format, integer) {
      if (integer == null) {
        // A sum or count over zero rows comes back as NULL; that is a zero, not "no value".
        // Dates have no meaningful zero, so they stay blank.
        if (data_format === 'date') return '';
        integer = 0;
      }

      if (data_format === 'currency') {
        return this.formatCurrency(integer);
      }

      if (data_format === 'date') {
        return date(integer, this.date_format);
      }

      if (data_format === 'percent') {
        return integer + '%';
      }

      if (data_format === 'hours') {
        return integer + 'h';
      }

      return integer;
    },
    formatCurrencyIn(code, amount) {
      // Same rules as formatCurrency, for a row that carries its own currency code.
      if (!code || code === this.currency) return this.formatCurrency(amount);
      if (amount == null || amount === '') return '';
      const value = parseFloat(amount);
      if (isNaN(value)) return amount;
      if (code === 'SEK' || code === 'DKK') {
        return `${value.toLocaleString(code === 'DKK' ? 'da-DK' : 'sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code === 'DKK' ? 'kr.' : 'kr'}`;
      }
      const match = _.find(this.currencies, { code });
      const symbol = match ? match.symbol : code + ' ';
      if (this.number_format === 1) {
        const localizedAmount = value.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
        return this.currency_format === 0 ? symbol + localizedAmount : localizedAmount + ' ' + code;
      }
      return this.currency_format === 0 ? symbol + value.toFixed(2) : value.toFixed(2) + ' ' + code;
    },
    formatCurrency(amount) {
      if (amount == null || amount === '') return '';

      const value = parseFloat(amount);
      if (isNaN(value)) return amount;

      // Handle SEK and DKK formatting (postfix krone symbols)
      if (this.currency === 'SEK' || this.currency === 'DKK') {
        const formattedAmount = value.toLocaleString(this.currency === 'DKK' ? 'da-DK' : 'sv-SE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        return `${formattedAmount} ${this.currency === 'DKK' ? 'kr.' : 'kr'}`;
      }

      // Handle other currency formatting based on existing logic
      if (this.number_format === 1) {
        const localizedAmount = value.toLocaleString(undefined, {
          minimumFractionDigits: 2
        }).replace('.', ',');

        if (this.currency_format === 0) {
          return this.currency_symbol + localizedAmount;
        } else {
          return localizedAmount + ' ' + this.currency;
        }
      }

      // Default formatting
      if (this.currency_format === 0) {
        return this.currency_symbol + value.toFixed(2);
      } else {
        return value.toFixed(2) + ' ' + this.currency;
      }
    },
    formatComparisonRange(startDate, endDate){
      // Comparison labels always carry the year; formatDateRange omits it for
      // same-year ranges, which makes multi-year overlays indistinguishable.
      const label = this.formatDateRange(startDate, endDate)
      if(!label) return label
      const start = moment(startDate, 'YYYY-MM-DD HH:mm:ss')
      const end = moment(endDate, 'YYYY-MM-DD HH:mm:ss')
      if(start.year() === end.year()){
        return label + ', ' + start.year()
      }
      return label
    },
    isBuilderCompareEligible(){
      // Mirrors isCompareEligible for the widget being configured: comparison
      // only exists on line/bar charts and ungrouped statistics, so the toggle
      // hides for pie, gauge, activity, and grouped configurations.
      if(!this.widget.widget_type) return false
      if(this.widget.widget_type.id === 3){
        return !this.widget.group_by_game && !this.widget.group_by_user && !this.widget.group_by_field
      }
      return this.widget.widget_type.id === 1 && this.widget.chart_type
        && (this.widget.chart_type.id === 1 || this.widget.chart_type.id === 2)
    },
    isGranularityEligible(item){
      return item.widget_type_id === 1 && item.chart_type_id === 1 && item.show_range_option && !item.is_all_time
    },
    isCompareEligible(item){
      if(!item.show_range_option || item.is_all_time) return false
      if(item.show_compare_option === false) return false
      if(!item.period || item.period.period_key === 'last_24_hours') return false
      if(item.widget_type_id === 3){
        return !item.group_by_game && !item.group_by_user && !item.group_by_field
      }
      return item.widget_type_id === 1 && (item.chart_type_id === 1 || item.chart_type_id === 2)
    },
    getComparisonSeries(item){
      if(!item.comparison || !item.comparison.periods) return []
      return item.comparison.periods.map(p => ({
        label: this.formatComparisonRange(p.start, p.end),
        data: p.data || []
      }))
    },
    getComparisonStat(period){
      if(!period || !period.data || !period.data[0]) return null
      const v = period.data[0].statistic
      return v === null || v === undefined ? null : Number(v)
    },
    getPercentChange(current, prior){
      if(prior === null || prior === 0 || current === null || isNaN(current)) return null
      return Math.round(((current - prior) / Math.abs(prior)) * 1000) / 10
    },
    getStatDelta(item, period){
      const current = item.data && item.data[0] && item.data[0].statistic !== null && item.data[0].statistic !== undefined
        ? Number(item.data[0].statistic) : null
      const pct = this.getPercentChange(current, this.getComparisonStat(period))
      if(pct === null) return null
      return { pct: pct, up: pct >= 0 }
    },
    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return null;
      const start = moment(startDate, 'YYYY-MM-DD HH:mm:ss');
      const end = moment(endDate, 'YYYY-MM-DD HH:mm:ss');
      if (!start.isValid() || !end.isValid()) return null;

      const sameYear = start.year() === end.year();
      const sameMonth = start.month() === end.month() && sameYear;

      let startFormat, endFormat;

      if (sameMonth) {
        // Same month and year: "Dec 5 - 11"
        startFormat = start.format('MMM D');
        endFormat = end.format('D');
      } else if (sameYear) {
        // Same year, different months: "Dec 5 - Jan 11"
        startFormat = start.format('MMM D');
        endFormat = end.format('MMM D');
      } else {
        // Different years: "Dec 28, 2024 - Jan 3, 2025"
        startFormat = start.format('MMM D, YYYY');
        endFormat = end.format('MMM D, YYYY');
      }

      return `${startFormat} - ${endFormat}`;
    },
    getWidgetDescription(item) {
      // Activity streams have no metric; describe the source and the window instead of
      // whatever metric fields the row happens to carry.
      if (item.widget_type_id === 2) {
        const sources = { 1: 'Event log', 2: 'Booking activity', 3: 'Transaction activity', 4: 'Customer emails' }
        const source = sources[Number(item.activity_source_id)] || 'Activity'
        return `${source} • Latest ${item.event_limit || 10} • Last ${item.activity_window_days || 90} days`
      }
      const parts = [];

      // Data source first
      const dataSources = {
        1: 'Bookings',
        3: 'Games',
        8: 'Checklists',
        13: 'Waivers',
        15: 'Payments',
        17: 'Purchases',
        18: 'Project tickets',
        19: 'Time logs'
      };
      if (item.table_id && dataSources[item.table_id]) {
        parts.push(dataSources[item.table_id]);
      }

      // Purchases bar/pie widgets are driven by the measurement dropdown, not the
      // metric type, so the description shows the measurement name (e.g. "Number of
      // sales", "Units sold", "Revenue (total)") instead of the generic metric text.
      if (item.table_id === 17 && (item.chart_type_id === 2 || item.chart_type_id === 3)
        && item.y_axis_dimension && item.y_axis_dimension.name) {
        parts.push(item.y_axis_dimension.name);
      }
      // Metric type + field name (combined without dot)
      else if (item.metric_type_id) {
        let metricText = '';
        const fieldName = item.field_name ? item.field_name.toLowerCase() : '';

        if (item.metric_type_id === 1) {
          metricText = 'Total count';
        } else if (item.metric_type_id === 2) {
          metricText = fieldName ? `Sum of ${fieldName}` : 'Sum of';
        } else if (item.metric_type_id === 3) {
          metricText = fieldName ? `Highest ${fieldName} value` : 'Highest value';
        } else if (item.metric_type_id === 4) {
          metricText = fieldName ? `Lowest ${fieldName} value` : 'Lowest value';
        } else if (item.metric_type_id === 5) {
          metricText = fieldName ? `Average of ${fieldName}` : 'Average of';
        }

        if (metricText) {
          parts.push(metricText);
        }
      }

      // Game filter - show prominently if filtered
      if (item.game_name && !item.group_by_game) {
        parts.push(`Filtered by ${item.game_name}`);
      }
      if (item.group_by_game) {
        parts.push('Grouped by event');
      }

      // Staff member filter - show if filtered
      if (item.company_user_name && !item.group_by_user) {
        parts.push(`Filtered by ${item.company_user_name}`);
      }
      if (item.group_by_user) {
        parts.push('Grouped by staff member');
      }
      if (item.group_by_field === 'currency') {
        parts.push('Grouped by currency');
      }

      // Payments data source: included payment methods
      if (item.table_id === 15 && item.purchase_item_filters) {
        try {
          const pf = typeof item.purchase_item_filters === 'string'
            ? JSON.parse(item.purchase_item_filters)
            : item.purchase_item_filters;
          if (pf && Array.isArray(pf.payment_types) && pf.payment_types.length > 0) {
            const labels = pf.payment_types.map(t => {
              const match = this.paymentMethodOptions.find(o => o.value === t);
              return match ? match.label.toLowerCase() : t.replace(/_/g, ' ');
            });
            parts.push(`Only ${labels.join(', ')}`);
          }
        } catch (e) {
          // ignore malformed filter json in description
        }
      }

      // Transactions data source: cancelled transactions are excluded unless the widget opts in
      if (item.table_id === 2 && item.purchase_item_filters) {
        try {
          const pf = typeof item.purchase_item_filters === 'string'
            ? JSON.parse(item.purchase_item_filters)
            : item.purchase_item_filters;
          if (pf && pf.include_cancelled_transactions) {
            parts.push('Including cancelled');
          }
        } catch (e) {
          // ignore malformed filter json in description
        }
      }

      // Promotion filter
      if (item.purchase_item_filters) {
        try {
          const pf = typeof item.purchase_item_filters === 'string'
            ? JSON.parse(item.purchase_item_filters)
            : item.purchase_item_filters;
          if (pf && Array.isArray(pf.promos) && pf.promos.length > 0) {
            const labels = pf.promos.map(ref => {
              const match = this.promoSelectOptions.find(o => o.value === ref);
              return match ? match.label : ref;
            });
            parts.push(labels.length > 2 ? `${labels.length} promotions` : `Promotion: ${labels.join(', ')}`);
          }
        } catch (e) {
          // ignore malformed filter json in description
        }
      }

      // Email template and template type filters
      if (item.purchase_item_filters) {
        try {
          const pf = typeof item.purchase_item_filters === 'string'
            ? JSON.parse(item.purchase_item_filters)
            : item.purchase_item_filters;
          if (pf && Array.isArray(pf.email_template_ids) && pf.email_template_ids.length > 0) {
            const labels = pf.email_template_ids.map(id => {
              const match = this.emailTemplateSelectOptions.find(o => Number(o.value) === Number(id));
              return match ? match.label : String(id);
            });
            parts.push(labels.length > 2 ? `${labels.length} templates` : `Template: ${labels.join(', ')}`);
          }
          if (pf && Array.isArray(pf.email_template_type_ids) && pf.email_template_type_ids.length > 0) {
            const labels = pf.email_template_type_ids.map(id => {
              const match = this.emailTemplateTypeSelectOptions.find(o => Number(o.value) === Number(id));
              return match ? match.label : String(id);
            });
            parts.push(labels.length > 2 ? `${labels.length} template types` : `Template type: ${labels.join(', ')}`);
          }
        } catch (e) {
          // ignore malformed filter json in description
        }
      }

      // Traffic attribution filters
      if (item.purchase_item_filters) {
        try {
          const pf = typeof item.purchase_item_filters === 'string'
            ? JSON.parse(item.purchase_item_filters)
            : item.purchase_item_filters;
          const utmPart = (arr, singular, plural) => {
            if (!Array.isArray(arr) || arr.length === 0) return;
            const labels = arr.map(v => formatUtmValue(v));
            parts.push(labels.length > 2 ? `${labels.length} ${plural}` : `${singular}: ${labels.join(', ')}`);
          };
          if (pf) {
            if (pf.utm_level === 'source_medium') parts.push('By source and medium');
            else if (pf.utm_level === 'campaign') parts.push('By source, medium and campaign');
            utmPart(pf.utm_sources, 'Source', 'sources');
            utmPart(pf.utm_mediums, 'Medium', 'mediums');
            utmPart(pf.utm_campaigns, 'Campaign', 'campaigns');
          }
        } catch (e) {
          // ignore malformed filter json in description
        }
      }

      // Time period with actual date range
      if (!item.is_all_time) {
        let periodText = '';
        if (item.period && (item.period.name || item.period.label)) {
          periodText = item.period.name || item.period.label;
        }

        // Add calculated date range if available
        const dateRange = this.formatDateRange(item.calculated_start_date, item.calculated_end_date);
        if (dateRange) {
          periodText = periodText ? `${periodText} (${dateRange})` : dateRange;
        }

        if (periodText) {
          parts.push(periodText);
        }
      } else {
        parts.push('All time');
      }

      if (item.compare_mode === 'previous_periods' && item.comparison) {
        parts.push('Compared to ' + (item.compare_count - 1) + ' previous period' + (item.compare_count > 2 ? 's' : ''));
      } else if (item.compare_mode === 'previous_years' && item.comparison) {
        parts.push('Compared to ' + (item.compare_count - 1) + ' previous year' + (item.compare_count > 2 ? 's' : ''));
      }

      if (item.scope === 'company') {
        const n = Array.isArray(item.company_group_ids) ? item.company_group_ids.length : 0;
        const access = item.scope_access
        if(access && access.excluded > 0){
          // The viewer's group access narrowed the widget: say so instead of pretending it is the full set.
          parts.push(access.visible + ' of ' + access.configured + ' groups (limited by your group access)');
        } else {
          parts.push(n > 0 ? n + (n === 1 ? ' group' : ' groups') : 'All groups');
        }
      }

      const gfl = this.globalFilterLine(item)
      if (gfl.some(f => f.applied)) {
        parts.push(gfl.some(f => !f.applied) ? 'Global filters partly applied' : 'Global filters applied');
      }

      const result = parts.join(' • ');
      return result;
    },
    updateMetricTypes(){
      this.widget.metric_type = {}
      // Custom-field statistic: the only meaningful metric is a count of responses.
      if(this.isCustomFieldSource(this.widget.source) && this.widget.widget_type && this.widget.widget_type.id === 3){
        const count = _.find(this.all_metric_types, {id: 1})
        this.metric_types = count ? [count] : []
        this.$set(this.widget, 'metric_type', count ? {id: count.id, name: count.name} : {})
        this.j++
        return
      }
      if(this.widget.hasOwnProperty('widget_type') && this.widget.widget_type.id === 3){
        if(this.widget.field.data_type === 'id'){
          this.metric_types = this.all_metric_types.filter(item => item.id === 1);
        } else if(this.widget.field.data_type === 'date'){
          this.metric_types = this.all_metric_types.filter(item => item.id === 3 || item.id === 4);
        } else if(this.widget.field.id === 56 || this.widget.field.id === 67){
          this.metric_types = this.all_metric_types.filter(item => item.id === 3 || item.id === 4 || item.id === 5);
        } else {
          this.metric_types = this.all_metric_types.filter(item => item.id !== 1)
        }
      } else if(this.widget.hasOwnProperty('widget_type') && this.widget.widget_type.id === 1
        && this.widget.chart_type.id === 1){

      }
      // A single valid metric (Count on an id field) is not a choice: select it, so the field
      // never sits empty and Update never fails on a metric the user could not have picked otherwise.
      if(this.widget.widget_type && this.widget.widget_type.id === 3 && this.metric_types.length === 1){
        const only = this.metric_types[0]
        this.$set(this.widget, 'metric_type', { id: only.id, name: only.name })
        this.errs.metric_type = ''
      }
      this.j++
    },
    updateFields(){
      this.widget.field = {}
      this.widget.metric_type = {}
      if(this.widget.hasOwnProperty('widget_type') && this.widget.widget_type.id === 1){
        if(this.widget.source){
          // Occupancy (117) is heatmap-only; the chart_type 5 branch below re-includes it.
          this.fields = this.all_fields.filter(item => item.table_id === this.widget.source.id && item.id !== 117);
        }

        // Resources keeps its numeric fields for every visualization. Default to the gauge when
        // no visualization is chosen yet, but don't override an explicit pick (e.g. line trend).
        if(this.isResourceSource(this.widget.source)){
          if(!this.widget.chart_type || !this.widget.chart_type.id){
            this.$set(this.widget, 'chart_type', { id: 4, name: 'Gauge' })
          }
        } else if(this.widget.hasOwnProperty('chart_type') && this.widget.chart_type.id === 2 && this.splitByGroup){
          // Bar split by group: any numeric field of the source, one bar per group, always over a period.
          this.fields = this.fields.filter(item => item.data_type !== 'collection' && item.id !== 134)
          this.widget.is_all_time = false
        } else if(this.widget.hasOwnProperty('chart_type') &&
          (this.widget.chart_type.id === 3 || this.widget.chart_type.id === 2)){
          this.fields = this.fields.filter(item => item.data_type === 'collection');
          this.widget.is_all_time = true
          // Update measurement options based on source
          if (this.widget.source && this.widget.source.id === 17) {
            this.currentMeasurements = [...this.purchaseMeasurements];
          } else if (this.widget.source && (this.widget.source.id === 18 || this.widget.source.id === 19)) {
            this.currentMeasurements = [...this.pmMeasurements];
          } else {
            this.currentMeasurements = [...this.measurements];
          }
          // Reset measurement only if current selection is invalid for new source
          const currentLabel = this.widget.y_axis_dimension ? this.widget.y_axis_dimension.label : null;
          const isValidForSource = this.currentMeasurements.some(m => m.label === currentLabel);
          if(!isValidForSource && this.currentMeasurements.length > 0){
            this.widget.y_axis_dimension = {
              label: this.currentMeasurements[0].label,
              name: this.currentMeasurements[0].name
            }
          }
        } else if(this.widget.hasOwnProperty('chart_type') && this.widget.chart_type.id === 4) {
          // Gauge on a ratio field: occupancy on Bookings, conversion rate on Carts; always over a period.
          const ratioField = this.widget.source && this.widget.source.id === 25 ? 114 : 117
          this.fields = this.all_fields.filter(item => item.id === ratioField)
          this.widget.is_all_time = false
        } else if(this.widget.hasOwnProperty('chart_type') && this.widget.chart_type.id === 6) {
          // Funnel: cart steps only, always over a period.
          this.fields = this.all_fields.filter(item => item.id === 116)
          this.widget.is_all_time = false
        } else if(this.widget.hasOwnProperty('chart_type') && this.widget.chart_type.id === 5) {
          // Heatmap: occupancy only, always over a period.
          this.fields = this.all_fields.filter(item => item.id === 117)
          this.widget.is_all_time = false
        } else if(this.widget.hasOwnProperty('chart_type') && this.widget.chart_type.id === 1) {
          this.fields = this.fields.filter(item => (item.data_type !== 'collection'
            && item.id !== 76 && item.id !== 77 && item.id !== 78 && item.id !== 66 && item.id !== 67
            && item.id !== 51 && item.id !== 56 && item.id !== 69 && item.id !== 68
            && item.id !== 107 && item.id !== 120 && item.id !== 134));
          this.widget.is_all_time = false
        } else {
          this.widget.is_all_time = true
        }

      }
      else if(this.widget.hasOwnProperty('widget_type') && this.widget.widget_type.id === 3){
        if(this.widget.source){
          this.fields = this.all_fields.filter(item => item.table_id === this.widget.source.id);
        }
        this.fields = this.fields.filter(item => item.data_type !== 'collection');
        this.widget.is_all_time = true
      } else {
        this.widget.is_all_time = true
      }
      // Company scope always uses a time period; the all-time switch is hidden.
      if(this.isCompanyScope){
        this.widget.is_all_time = false
      }
      this.widget.field = this.fields.length > 0 ? this.fields[0] : ''

      // Custom-field source handling.
      if(this.widget.source && this.isCustomFieldSource(this.widget.source) && this.widget.widget_type){
        if(this.customFieldOptions.length === 0){
          this.fetchCustomFieldsForStats()
        }
        if(this.widget.widget_type.id === 1){
          // Distribution: force a pie/bar chart and keep the synthetic "Response" (collection) field.
          if(!this.widget.chart_type || this.widget.chart_type.id === 1){
            const pie = _.find(this.chart_types, {id: 3})
            if(pie){ this.$set(this.widget, 'chart_type', {id: pie.id, name: pie.name}) }
          }
          this.fields = this.all_fields.filter(item => item.table_id === this.widget.source.id && item.data_type === 'collection')
          this.widget.field = this.fields.length > 0 ? this.fields[0] : ''
        } else if(this.widget.widget_type.id === 3){
          // Statistic: count of responses. Keep the Response field (statistic normally drops
          // collection fields) and force the Count metric (see updateMetricTypes).
          this.fields = this.all_fields.filter(item => item.table_id === this.widget.source.id)
          this.widget.field = this.fields.length > 0 ? this.fields[0] : ''
          this.updateMetricTypes()
        }
      }
      this.j++
    },
    editWidget(item, event){
      event.preventDefault()

      // Vue 2 reactivity: item (the layout row) has no source/widget_type/chart_type/etc keys,
      // so adding them AFTER the spread makes them non-reactive — that broke chart-type
      // visibility when switching the data source in edit mode (both directions). Build every
      // derived field INTO the initial assignment so the el-select bindings stay reactive.
      this.widget = {
        ...item,
        widget_type: _.find(this.widget_types, {id: item.widget_type_id}),
        chart_type: _.find(this.chart_types, {id: item.chart_type_id}),
        source: _.find(this.sources, {id: item.table_id}),
        activity_source: _.find(this.activity_sources, {id: item.activity_source_id}),
        period: _.find(this.time_period_types, {id: item.time_period_type_id}) || {id: 1},
        field: {},
        metric_type: {},
        game_id: item.game_id || null,
        group_by_game: Boolean(item.group_by_game),
        company_user_id: item.company_user_id || null,
        group_by_user: Boolean(item.group_by_user),
        custom_field_id: item.custom_field_id || null,
        show_description_label: item.show_description_label !== undefined ? Boolean(item.show_description_label) : true,
        allow_export: item.allow_export !== undefined ? Boolean(item.allow_export) : true,
        show_compare_option: item.show_compare_option !== undefined ? Boolean(item.show_compare_option) : true,
        default_granularity: ['day', 'week', 'month'].includes(item.default_granularity) ? item.default_granularity : 'auto',
        default_compare_mode: ['previous_periods', 'previous_years'].includes(item.default_compare_mode) ? item.default_compare_mode : 'none',
        default_compare_count: [2,3,4,5,6,7,8,9,10].includes(Number(item.default_compare_count)) ? Number(item.default_compare_count) : 2,
        scope: item.scope === 'company' ? 'company' : 'group',
        company_group_ids: Array.isArray(item.company_group_ids) ? [...item.company_group_ids] : []
      }
      if(this.widget.scope === 'company' && this.allGroups.length === 0){
        this.getGroups()
      }
      this.fields = this.all_fields.filter(it => it.table_id === item.table_id);

      this.updateFields()
      this.$set(this.widget, 'field', _.find(this.fields, {id: item.field_id}))
      this.updateMetricTypes()
      this.$set(this.widget, 'metric_type', _.find(this.all_metric_types, {id: item.metric_type_id}))
      // is_all_time / show_range_option are reset by updateFields, so apply the saved values after it.
      this.$set(this.widget, 'is_all_time', Boolean(item.is_all_time))
      this.$set(this.widget, 'show_range_option', Boolean(item.show_range_option))

      if(this.isCustomFieldSource(this.widget.source)){
        this.fetchCustomFieldsForStats()
      }

      // Set purchase types from saved string (for Purchases data source)
      this.setPurchaseTypesFromString(item.purchase_types);
      this.setPurchaseItemFiltersFromJson(item.purchase_item_filters);

      // Set currentMeasurements based on data source
      if (item.table_id === 17) {
        this.currentMeasurements = [...this.purchaseMeasurements];
      } else if (item.table_id === 18 || item.table_id === 19) {
        this.currentMeasurements = [...this.pmMeasurements];
      } else {
        this.currentMeasurements = [...this.measurements];
      }

      this.modals.new_widget = true

      this.j++
    },
    applyConfigToForm(payload){
      // Apply an AI-generated widget config (real ids, resolved server-side) into the open
      // form, driving the existing cascade so validity is enforced. Pre-fills; user edits + saves.
      const config = payload && payload.config ? payload.config : payload
      if(!config) return

      if(config.title) this.$set(this.widget, 'title', config.title)
      if(config.label !== undefined && config.label !== null) this.$set(this.widget, 'label', config.label)
      if(config.highlight_color) this.$set(this.widget, 'highlight_color', config.highlight_color)

      // Scope before the cascade: isCompanyScope gates sources, chart types, and all-time.
      if(config.scope === 'company' || config.scope === 'group'){
        this.$set(this.widget, 'scope', config.scope)
        if(config.scope === 'company' && this.allGroups.length === 0){
          this.getGroups()
        }
      }

      // Widget type + chart type first (they gate which fields render and how the cascade fills).
      const wt = _.find(this.widget_types, {id: config.widget_type})
      if(wt) this.$set(this.widget, 'widget_type', {id: wt.id, name: wt.name})
      if(config.widget_type === 1 && config.chart_type){
        const ct = _.find(this.chart_types, {id: config.chart_type})
        if(ct) this.$set(this.widget, 'chart_type', {id: ct.id, name: ct.name})
      }

      // Data source, then run the cascade to build the field list + source-dependent state.
      this.$set(this.widget, 'source', _.find(this.sources, {id: config.table_id}) || '')
      this.updateFields()

      if(config.field_id){
        const f = _.find(this.fields, {id: config.field_id})
        if(f) this.$set(this.widget, 'field', f)
      }
      this.updateMetricTypes()
      if(config.metric_type){
        const mt = _.find(this.all_metric_types, {id: config.metric_type})
        if(mt) this.$set(this.widget, 'metric_type', {id: mt.id, name: mt.name})
      }

      // Custom field selection — fetch loads the picker + reconciles the id to the kept representative.
      if(this.isCustomFieldSource(this.widget.source)){
        this.$set(this.widget, 'custom_field_id', config.custom_field_id || null)
        this.fetchCustomFieldsForStats()
      }

      this.$set(this.widget, 'group_by_game', !!config.group_by_game)
      this.$set(this.widget, 'game_id', config.game_id || null)

      this.$set(this.widget, 'is_all_time', !!config.is_all_time)
      if(config.time_period_type_id){
        this.$set(this.widget, 'period', _.find(this.time_period_types, {id: config.time_period_type_id}))
      }

      this.j++
    },
    async saveDashboardSettings(payload){
      // One drawer, four backend actions: only the sections that changed are sent, in order.
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let dashboardId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
        + '/dashboards/' + dashboardId

      const current = this.dashboard
      const actions = []
      if(payload.name !== (current.name || '') || (payload.description || '') !== (current.description || '')){
        actions.push({ action: 'rename', name: payload.name, description: payload.description })
      }
      if(payload.refresh_enabled !== Boolean(current.refresh_enabled) || String(payload.refresh_interval_minutes) !== String(current.refresh_interval_minutes == null ? '' : current.refresh_interval_minutes)){
        actions.push({ action: 'refresh', refresh_enabled: payload.refresh_enabled, refresh_interval_minutes: payload.refresh_interval_minutes })
      }
      const rolesChanged = this.rolesLoaded && JSON.stringify(payload.allowed_roles) !== JSON.stringify(this.allowedRoles)
      if(payload.accessible_by_all_users !== Boolean(current.accessible_by_all_users) || JSON.stringify(payload.allowed_users) !== JSON.stringify(this.currentAllowedUsers()) || rolesChanged){
        const permissions = { action: 'permissions', accessible_by_all_users: payload.accessible_by_all_users, allowed_users: payload.allowed_users }
        if(this.rolesLoaded) permissions.allowed_roles = payload.allowed_roles
        actions.push(permissions)
      }
      if(JSON.stringify(payload.filter_bar) !== JSON.stringify(this.filterBarConfig)){
        actions.push({ action: 'filter_bar', filter_bar: payload.filter_bar })
      }

      this.settingsSaving = true
      try {
        for (const data of actions) {
          await this.axios.put(url, data, config)
        }
        this.settingsDrawerOpen = false
        this.alertDialog = { show: true, title: 'Success', message: actions.length > 0 ? 'Dashboard has been updated' : 'No changes to save', variant: 'success' }
        if(actions.length > 0) this.getDashboard()
      } catch(err) {
        this.loading = false
        console.log(err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/forbidden')
        } else {
          this.alertDialog = { show: true, title: 'Error', message: 'Something went wrong', variant: 'danger' }
        }
      } finally {
        this.settingsSaving = false
      }
    },
    currentAllowedUsers(){
      // Same shape the drawer emits, built from the loaded viewer/editor lists.
      const allowed = {}
      for (const u of this.viewers) {
        if (!allowed[u.id]) allowed[u.id] = { id: u.id, can_read: true, can_write: false }
        else allowed[u.id].can_read = true
      }
      for (const u of this.editors) {
        if (!allowed[u.id]) allowed[u.id] = { id: u.id, can_read: false, can_write: true }
        else allowed[u.id].can_write = true
      }
      return Object.values(allowed)
    },
    onViewportChange(){
      const next = viewportColumns(window.innerWidth)
      if(next !== this.viewportCols){
        this.viewportCols = next
        this.$nextTick(() => this.onWidgetResized())
      }
    },
    onGridLayoutUpdated(){
      // vue-grid-layout emits layout-updated while the initial layout is being assembled; only
      // user drags/resizes after the load may persist, never a transient frame or a repack.
      if(!this.layoutReady || this.viewportCols !== 0) return
      this.updateLayout()
    },
    async updateLayout(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let dashboardId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
        + '/dashboards/' + dashboardId + '/widgets'

      let data = {
        widgets: this.layout,
        grid_cols: this.gridCols
      }

      this.axios.put(url, data, config)
        .then(response => {
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if (err.response.status == 401) {
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          }
        })
    },
    async updateWidget(item, action){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      const dashboardId = this.$route.query.id
      const widgetId = item ? item.id : this.widget.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
        + '/dashboards/' + dashboardId + '/widgets/' +widgetId

      let data = {}

      if(action === 'highlight_color'){
        data = {
          action: 'highlight_color',
          highlight_color: item.highlight_color
        }
      }
      else if(action === 'period'){
        data = {
          action: 'period',
          time_period_type_id: item.period.id,
          start_date: item.start_date,
          end_date: item.end_date
        }
      }
      else {
        if(!this.validateFields(item)){
          return
        }
        this.loading = true
        data = {
          widget_type: this.widget.widget_type.id,
          label: this.widget.label,
          title: this.widget.title,
          metric_type: this.widget.metric_type ? this.widget.metric_type.id : null,
          chart_type: this.widget.chart_type ? this.widget.chart_type.id : null,
          table_id: this.widget.source ? this.widget.source.id : null ,
          field_id: this.widget.field ? this.widget.field.id : null,
          is_all_time: this.widget.is_all_time,
          start_date: this.widget.start_date,
          end_date: this.widget.end_date,
          y_axis_dimension: this.widget.y_axis_dimension && this.widget.y_axis_dimension.label ? this.widget.y_axis_dimension.label : 'count',
          time_period_type_id: this.widget.period ? this.widget.period.id : 1,
          show_range_option: this.widget.show_range_option ? this.widget.show_range_option : 0,
          show_compare_option: this.widget.show_compare_option === false ? 0 : 1,
          default_granularity: this.widget.default_granularity || 'auto',
          default_compare_mode: this.widget.default_compare_mode || 'none',
          default_compare_count: this.widget.default_compare_count || 2,
          activity_source: this.widget.activity_source ? this.widget.activity_source.id : null,
          event_limit: this.widget.event_limit ? this.widget.event_limit : null,
          game_id: this.widget.game_id || null,
          group_by_game: this.widget.group_by_game ? 1 : 0,
          company_user_id: this.widget.company_user_id || null,
          group_by_user: this.widget.group_by_user ? 1 : 0,
          group_by_field: this.widget.group_by_field || null,
          custom_field_id: this.isCustomFieldSource(this.widget.source) ? (this.widget.custom_field_id || null) : null,
          show_description_label: this.widget.show_description_label ? 1 : 0,
          allow_export: this.widget.allow_export ? 1 : 0,
          scope: this.widget.scope === 'company' ? 'company' : 'group',
          company_group_ids: this.widget.scope === 'company' && Array.isArray(this.widget.company_group_ids) && this.widget.company_group_ids.length > 0 ? this.widget.company_group_ids : null,
          purchase_types: this.getPurchaseTypesString(),
          purchase_item_filters: this.getPurchaseItemFilters()
        }
      }

      this.axios.put(url, data, config)
        .then(response => {
          this.loading = false
          this.widget = ''
          this.modals.new_widget = false
          this.getDashboard()
          this.j++
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if (err.response.status == 401) {
            this.$router.push('/forbidden')
          } else if(err.response.status == 400){
            this.$notify({ type: 'danger', message: typeof err.response.data === 'string' ? err.response.data : 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          }
        })
    },
    async deleteDashboard(event){
      event.preventDefault()

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + this.dashboard.id

      this.axios.delete(url, config)
        .then(response => {
          this.loading = false
          this.$notify({ type: 'success', message: 'Dashboard has been deleted', icon: 'tim-icons icon-check-2' })
          this.$router.push('/dashboards/settings')
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else  if(err.response.status == 500){
            this.$notify({ type: 'danger', message: typeof err.response.data === 'string' ? err.response.data : 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          }
        })
    },
    async deleteWidget(item, event){
      event.preventDefault()
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let dashboardId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/'
        + dashboardId + '/widgets/' + item.id

      this.axios.delete(url, config)
        .then(response => {
          this.loading = false
          this.getDashboard()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if (err.response.status == 401) {
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          }
        })
    },
    exportWidgetData(item, event){
      event.preventDefault()
      this.exportWidget = item
      this.exportFormat = 'csv'
      this.modals.export = true
    },
    async performExport(){
      if (!this.exportWidget) return

      try {
        const item = this.exportWidget
        // Generate filename
        const periodLabel = item.period ? item.period.name || item.period.label : 'All Time'
        const filename = `${item.title} - ${periodLabel}`.replace(/[^a-z0-9\s\-]/gi, '_')
        const timestamp = new Date().toISOString().split('T')[0]

        if (this.exportFormat === 'csv') {
          this.exportAsCSV(item, `${filename}_${timestamp}.csv`)
        } else if (this.exportFormat === 'json') {
          this.exportAsJSON(item, `${filename}_${timestamp}.json`)
        } else if (this.exportFormat === 'png') {
          await this.exportAsPNG(item, `${filename}_${timestamp}.png`)
        }

        this.modals.export = false
        this.exportWidget = null
        this.$notify({ type: 'success', message: 'Widget data exported successfully', icon: 'tim-icons icon-check-2' })
      } catch (err) {
        console.error('Export error:', err)
        this.$notify({ type: 'danger', message: 'Failed to export widget data', icon: 'tim-icons icon-alert-circle-exc' })
      }
    },
    exportAsCSV(item, filename){
      let csvContent = '';

      // Handle different widget types
      if (item.widget_type_id === 1) {
        // Chart widget - has labels and values
        if (!item.data || item.data.length === 0) {
          throw new Error('No data to export')
        }

        // CSV Header
        csvContent = 'Label,Value\n';

        // CSV Rows
        item.data.forEach(row => {
          const label = row.label || row.x || ''
          const value = row.value || row.y || 0
          csvContent += `"${label}",${value}\n`
        })
      } else if (item.widget_type_id === 3) {
        // Statistics widget
        if (item.group_by_game && item.data && Array.isArray(item.data)) {
          // Grouped by game
          csvContent = 'Game,Value\n';
          item.data.forEach(row => {
            csvContent += `"${row.game_name || 'Unknown'}",${row.statistic || 0}\n`
          })
        } else if (item.group_by_user && item.data && Array.isArray(item.data)) {
          // Grouped by staff member
          csvContent = 'Staff Member,Value\n';
          item.data.forEach(row => {
            csvContent += `"${row.user_name || 'Unknown'}",${row.statistic || 0}\n`
          })
        } else if (item.group_by_field && item.data && Array.isArray(item.data)) {
          // Grouped by field (currency, PM breakdown)
          csvContent = 'Group,Value\n';
          item.data.forEach(row => {
            csvContent += `"${row.label || 'Unknown'}",${row.statistic || 0}\n`
          })
        } else {
          // Single statistic
          csvContent = 'Metric,Value\n';
          csvContent += `"${item.title}",${item.statistic || 0}\n`
        }
      } else if (item.widget_type_id === 2) {
        // Activity widget
        if (!item.data || item.data.length === 0) {
          throw new Error('No data to export')
        }

        // Get all unique keys from the data
        const keys = Object.keys(item.data[0] || {})
        csvContent = keys.join(',') + '\n'

        item.data.forEach(row => {
          const values = keys.map(key => {
            const val = row[key]
            // Wrap in quotes and escape existing quotes
            return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val
          })
          csvContent += values.join(',') + '\n'
        })
      }

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    exportAsJSON(item, filename){
      const exportData = {
        widget: {
          title: item.title,
          label: item.label,
          type: item.widget_type_id,
          period: item.period ? (item.period.name || item.period.label) : 'All Time',
          exported_at: new Date().toISOString()
        },
        data: item.data || item.statistic || null
      }

      if (item.game_name) {
        exportData.widget.game = item.game_name
      }

      const jsonString = JSON.stringify(exportData, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    async exportAsPNG(item, filename){
      const widgetElement = document.querySelector(`[data-widget-id="widget-${item.id}"]`)

      if (!widgetElement) {
        throw new Error('Widget element not found')
      }

      // Capture the widget as canvas
      const canvas = await html2canvas(widgetElement, {
        backgroundColor: '#1e1e2e',
        scale: 2, // Higher quality
        logging: false,
        useCORS: true
      })

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }, 'image/png')
    },
    validateFields(item){
      let errors = 0

      if(!item.widget_type){
        this.errs.widget_type = 'Widget type cannot be empty'
        errors++
      } else {
        this.errs.widget_type = ''
      }

      if(item.hasOwnProperty('widget_type') && item.widget_type.id === 1 && !item.chart_type){
        this.errs.chart_type = 'Chart type cannot be empty'
        errors++
      } else {
        this.errs.chart_type = ''
      }

      if(item.hasOwnProperty('widget_type') && (item.widget_type.id === 1 || item.widget_type.id === 3)
        && !item.source){
        this.errs.data_source = 'Data source cannot be empty'
        errors++
      } else {
        this.errs.data_source = ''
      }

      if(item.hasOwnProperty('source') && (item.widget_type.id === 1 || item.widget_type.id === 3)
        && Object.keys(item.field).length === 0){
        this.errs.field = 'Data field cannot be empty'
        errors++
      } else {
        this.errs.field = ''
      }

      if(item.hasOwnProperty('widget_type') && item.widget_type.id === 3 && !this.isResourceSource(item.source)
        && Object.keys(item.metric_type).length === 0){
        this.errs.metric_type = 'Metric type cannot be empty'
        errors++
      } else {
        this.errs.metric_type = ''
      }

      if(this.isCustomFieldSource(item.source) && !item.custom_field_id){
        this.errs.custom_field = 'Custom field cannot be empty'
        errors++
      } else {
        this.errs.custom_field = ''
      }

      if(item.hasOwnProperty('widget_type') && item.widget_type.id === 2 && !item.activity_source){
        this.errs.activity_source = 'Activity source cannot be empty'
        errors++
      } else {
        this.errs.activity_source = ''
      }

      if(item.hasOwnProperty('widget_type') && item.widget_type.id === 2 &&
        (!item.event_limit || item.event_limit<= 0 || isNaN(item.event_limit))){
        this.errs.number_of_events = 'Invalid event limit'
        errors++
      } else if(item.hasOwnProperty('widget_type') && item.widget_type.id === 2 && item.event_limit > 100){
        this.errs.number_of_events = 'Event limit cannot exceed 100'
        errors++
      } else {
        this.errs.number_of_events = ''
      }

      return errors > 0 ? false : true
    },
    async createWidget(){
      if(!this.validateFields(this.widget)){
        return
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let dashboardId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + dashboardId + '/widgets'

      let data = {
        widget_type: this.widget.widget_type.id,
        label: this.widget.label ? this.widget.label : '',
        title: this.widget.title ? this.widget.title : '',
        x: 0,
        y: this.nextFreeRow(),                                           // True bottom of the grid, never inside another tile
        w: this.widget.widget_type.id === 4 ? this.gridCols : this.gridCols / 3,
        h: this.widget.widget_type.id === 4 ? 4 : 20,
        metric_type: this.widget.metric_type ? this.widget.metric_type.id : null,
        chart_type: this.widget.chart_type ? this.widget.chart_type.id : null,
        table_id: this.widget.source ? this.widget.source.id : null ,
        field_id: this.widget.field ? this.widget.field.id : null,
        is_all_time: this.widget.is_all_time,
        start_date: this.widget.start_date,
        end_date: this.widget.end_date,
        y_axis_dimension: this.widget.y_axis_dimension && this.widget.y_axis_dimension.label ? this.widget.y_axis_dimension.label : 'count',
        time_period_type_id: this.widget.period ? this.widget.period.id : 1,
        show_range_option: this.widget.show_range_option ? this.widget.show_range_option : 0,
        show_compare_option: this.widget.show_compare_option === false ? 0 : 1,
        default_granularity: this.widget.default_granularity || 'auto',
        default_compare_mode: this.widget.default_compare_mode || 'none',
        default_compare_count: this.widget.default_compare_count || 2,
        activity_source: this.widget.activity_source ? this.widget.activity_source.id : null,
        event_limit: this.widget.event_limit ? this.widget.event_limit : null,
        game_id: this.widget.game_id || null,
        group_by_game: this.widget.group_by_game ? 1 : 0,
        company_user_id: this.widget.company_user_id || null,
        group_by_user: this.widget.group_by_user ? 1 : 0,
        group_by_field: this.widget.group_by_field || null,
        custom_field_id: this.isCustomFieldSource(this.widget.source) ? (this.widget.custom_field_id || null) : null,
        show_description_label: this.widget.show_description_label ? 1 : 0,
        allow_export: this.widget.allow_export ? 1 : 0,
        scope: this.widget.scope === 'company' ? 'company' : 'group',
        company_group_ids: this.widget.scope === 'company' && Array.isArray(this.widget.company_group_ids) && this.widget.company_group_ids.length > 0 ? this.widget.company_group_ids : null,
        purchase_types: this.getPurchaseTypesString(),
        purchase_item_filters: this.getPurchaseItemFilters(),
        highlight_color: typeof this.widget.highlight_color === 'string' && /^#[0-9a-fA-F]{6}$/.test(this.widget.highlight_color) ? this.widget.highlight_color : null
      }
      this.axios.post(url, data, config)
        .then(response => {
          this.loading = false
          this.modals.new_widget = false
          this.getDashboard()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if (err.response.status == 401) {
            this.$router.push('/forbidden')
          } else if(err.response.status == 400){
            this.$notify({ type: 'danger', message: typeof err.response.data === 'string' ? err.response.data : 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          }
        })
    },
    async duplicateWidget(item, event){
      event.preventDefault()
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let dashboardId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + dashboardId + '/widgets'

      // Rebuild the create payload from the existing layout item (it already carries the full
      // config from view-dashboard-by-id). Fresh x/y so the copy lands at the bottom instead of
      // overlapping the original; everything else is an exact duplicate.
      let data = {
        widget_type: item.widget_type_id,
        label: item.label || '',
        title: item.title || '',
        x: 0,
        y: this.nextFreeRow(),
        w: item.w || this.gridCols / 3,
        h: item.h || 20,
        metric_type: item.metric_type_id || null,
        chart_type: item.chart_type_id || null,
        table_id: item.table_id || null,
        field_id: item.field_id || null,
        is_all_time: item.is_all_time ? 1 : 0,
        start_date: item.start_date ? moment(item.start_date).format('YYYY-MM-DD') : null,
        end_date: item.end_date ? moment(item.end_date).format('YYYY-MM-DD') : null,
        y_axis_dimension: item.y_axis_dimension && item.y_axis_dimension.label ? item.y_axis_dimension.label : 'count',
        time_period_type_id: item.time_period_type_id || 1,
        show_range_option: item.show_range_option ? 1 : 0,
        show_compare_option: item.show_compare_option === false ? 0 : 1,
        default_granularity: ['day', 'week', 'month'].includes(item.default_granularity) ? item.default_granularity : 'auto',
        default_compare_mode: ['previous_periods', 'previous_years'].includes(item.default_compare_mode) ? item.default_compare_mode : 'none',
        default_compare_count: [2,3,4,5,6,7,8,9,10].includes(Number(item.default_compare_count)) ? Number(item.default_compare_count) : 2,
        activity_source: item.activity_source_id || null,
        event_limit: item.event_limit || null,
        game_id: item.game_id || null,
        group_by_game: item.group_by_game ? 1 : 0,
        company_user_id: item.company_user_id || null,
        group_by_user: item.group_by_user ? 1 : 0,
        group_by_field: item.group_by_field || null,
        custom_field_id: item.custom_field_id || null,
        show_description_label: item.show_description_label ? 1 : 0,
        allow_export: item.allow_export ? 1 : 0,
        purchase_types: item.purchase_types || null,
        purchase_item_filters: item.purchase_item_filters || null,
        scope: item.scope === 'company' ? 'company' : 'group',
        company_group_ids: item.scope === 'company' && Array.isArray(item.company_group_ids) && item.company_group_ids.length > 0 ? [...item.company_group_ids] : null
      }

      try {
        await this.axios.post(url, data, config)
        this.loading = false
        this.getDashboard()
      } catch (err) {
        this.loading = false
        console.log(err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        }
      }
    },
    async duplicateDashboard(event){
      event.preventDefault()
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let dashboardId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + dashboardId + '/duplicate'

      try {
        // Same-group duplicate: the current group is the only target.
        let response = await this.axios.post(url, { groups: [companyGroupId] }, config)
        this.loading = false
        const newId = response.data && response.data.dashboards && response.data.dashboards[0]
          ? response.data.dashboards[0].dashboard_id : null
        if(newId){
          // Await the route change so getDashboard() reads the new query id, then reload
          // (same path + new query won't remount the component on its own).
          await this.$router.push({ path: '/dashboards/edit', query: { id: newId } }).catch(() => {})
          this.getDashboard(true)
        } else {
          this.$notify({ type: 'success', message: 'Dashboard has been duplicated', icon: 'tim-icons icon-check-2' })
        }
      } catch (err) {
        this.loading = false
        console.log(err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        }
      }
    },
    async copyToGroupsPrompt(event){
      event.preventDefault()
      await this.getGroups()
      this.showCopyDrawer = true
    },
    async getGroups(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups'
      try {
        let response = await this.axios.get(url, config)
        this.allGroups = response.data
      } catch (err) {
        console.log(err)
      }
    },
    async copyToGroups(groupIds){
      if(!groupIds || groupIds.length === 0){
        return
      }
      this.copyLoading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let dashboardId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + dashboardId + '/duplicate'

      try {
        let response = await this.axios.post(url, { groups: groupIds }, config)
        let copied = response.data && response.data.copied ? response.data.copied : groupIds.length
        this.copyLoading = false
        this.showCopyDrawer = false
        this.$notify({ type: 'success', message: copied === 1 ? 'Dashboard has been copied to 1 group' : 'Dashboard has been copied to ' + copied + ' groups', icon: 'tim-icons icon-check-2' })
      } catch (err) {
        console.log(err)
        this.copyLoading = false
        if(err.response && err.response.status === 401){
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        }
      }
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async loadWidgetData(item, updatePeriod = false) {
      if(item.widget_type_id === 4) return   // sections carry no data
      // Prevent duplicate calls - check if already loading
      if (this.widgetLoadingStates[item.id]) {
        return
      }

      // Set loading state for this widget
      this.$set(this.widgetLoadingStates, item.id, true)

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let dashboardId = this.$route.query.id
      const tz = moment().utcOffset();
      const gq = this.filterBarConfig.enabled ? buildWidgetQuery(this.filterValues, item, this.time_period_types) : { dateParams: '', filtersParam: '' }
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
        + '/dashboards/' + dashboardId + '/widgets/' + item.id + '?tz=' + tz
      url += gq.dateParams !== '' ? gq.dateParams
        : ('&time_period_type=' + item.period.period_key + '&time_period_type_id=' + item.period.id
          + '&start_date=' + moment(item.start_date, 'YYYY-MM-DD').format('YYYY-MM-DD')
          + '&end_date=' + moment(item.end_date, 'YYYY-MM-DD').format('YYYY-MM-DD'))
      url += gq.filtersParam

      if(item.compare_mode && item.compare_mode !== 'none' && this.isCompareEligible(item)){
        url += '&compare_mode=' + item.compare_mode + '&compare_count=' + item.compare_count
      }

      if(this.isGranularityEligible(item) && item.granularity){
        url += '&granularity=' + item.granularity
      }

      try {
        const response = await this.axios.get(url, config)

        // Find the widget in layout and update it
        const layoutIndex = this.layout.findIndex(w => w.id === response.data.widget.id)
        if (layoutIndex !== -1) {
          // Use Vue.set to ensure reactivity
          this.$set(this.layout[layoutIndex], 'data', response.data.widget.data)
          this.$set(this.layout[layoutIndex], 'data_type', response.data.widget.data_type)
          // Activity streams: the data lambda clamps event_limit and reports the window it applied.
          if (response.data.widget.activity_source_id != null) this.$set(this.layout[layoutIndex], 'activity_source_id', Number(response.data.widget.activity_source_id))
          if (response.data.widget.event_limit != null) this.$set(this.layout[layoutIndex], 'event_limit', response.data.widget.event_limit)
          if (response.data.widget.activity_window_days != null) this.$set(this.layout[layoutIndex], 'activity_window_days', response.data.widget.activity_window_days)
          this.$set(this.layout[layoutIndex], 'scope_access', response.data.widget.scope_access || null)
          this.$set(this.layout[layoutIndex], 'game_id', response.data.widget.game_id || null)
          this.$set(this.layout[layoutIndex], 'game_name', response.data.widget.game_name || null)
          this.$set(this.layout[layoutIndex], 'group_by_game', Boolean(response.data.widget.group_by_game))
          this.$set(this.layout[layoutIndex], 'company_user_id', response.data.widget.company_user_id || null)
          this.$set(this.layout[layoutIndex], 'group_by_user', Boolean(response.data.widget.group_by_user))
          this.$set(this.layout[layoutIndex], 'group_by_field', response.data.widget.group_by_field || null)
          this.$set(this.layout[layoutIndex], 'company_user_name', response.data.widget.company_user_name || null)
          this.$set(this.layout[layoutIndex], 'field_name', response.data.widget.field_name || null)
          this.$set(this.layout[layoutIndex], 'comparison', response.data.comparison || null)
          this.$set(this.layout[layoutIndex], 'applied_granularity', response.data.granularity || 'day')
          this.$set(this.layout[layoutIndex], 'show_description_label', response.data.widget.show_description_label !== undefined ? Boolean(response.data.widget.show_description_label) : true)
          this.$set(this.layout[layoutIndex], 'allow_export', response.data.widget.allow_export !== undefined ? Boolean(response.data.widget.allow_export) : true)
          this.$set(this.layout[layoutIndex], 'purchase_types', response.data.widget.purchase_types || null)

          // Store calculated date range from backend
          this.$set(this.layout[layoutIndex], 'calculated_start_date', response.data.calculated_start_date || null)
          this.$set(this.layout[layoutIndex], 'calculated_end_date', response.data.calculated_end_date || null)
          this.$set(this.layout[layoutIndex], 'filter_state', response.data.widget.filter_state || null)

          if(updatePeriod) {
            this.$set(this.layout[layoutIndex], 'start_date', item.start_date)
            this.$set(this.layout[layoutIndex], 'end_date', item.end_date)
            this.$set(this.layout[layoutIndex], 'h', item.h)
            this.$set(this.layout[layoutIndex], 'is_all_time', Boolean(response.data.widget.is_all_time))
            this.$set(this.layout[layoutIndex], 'period', _.find(this.time_period_types, {id: response.data.widget.time_period_type_id}))
          }
        }
      } catch(err) {
        console.log(err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/forbidden')
        } else if(err.response && err.response.status == 400){
          this.$notify({ type: 'danger', message: typeof err.response.data === 'string' ? err.response.data : 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong loading widget data', icon: 'tim-icons icon-alert-circle-exc' })
        }
      } finally {
        this.$set(this.widgetLoadingStates, item.id, false)
      }
    },
    async getWidgetData(item){
      await this.loadWidgetData(item, true)
    },
    async getDashboard(init) {
      this.layoutReady = false
      if(init){
        this.loading = true
      }

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let dashboardId = this.$route.query.id
      const tz = moment().utcOffset();

      // Load dashboard metadata only (no widget data)
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + dashboardId + '?tz=' + tz + '&metadata_only=true&include_access=true'

      try {
        const response = await this.axios.get(url, config)
        this.loading = false
        this.dashboard = response.data.dashboard
        this.dashboard.refresh_enabled = Boolean(this.dashboard.refresh_enabled)
        this.filterBarConfig = normalizeConfig(this.dashboard.filter_bar)
        this.filterOptions = response.data.filter_options || null
        this.filterValues = JSON.parse(JSON.stringify(this.filterBarConfig.defaults))
        this.filterBarKey++
        // Self-heal legacy 6-column dashboards: double x/w in memory (visually identical at
        // 12 columns) and persist the upgrade below, once the layout is built.
        const legacyGrid = Number(this.dashboard.grid_cols) !== 12
        if(legacyGrid){
          this.$set(this.dashboard, 'grid_cols', 12)
        }
        this.viewers = []
        this.editors = []
        for(let n in response.data.allowed_users){
          if(response.data.allowed_users[n].can_read){
            this.viewers.push(response.data.allowed_users[n])
          }
          if(response.data.allowed_users[n].can_write){
            this.editors.push(response.data.allowed_users[n])
          }
        }
        // Role selections and the roles the drawer can offer (OTC-3138); returned to editors only.
        // rolesLoaded is false when the lambda sent no role lists (an older view-dashboard-by-id); the
        // save then leaves role links alone instead of sending an empty list that would clear them.
        this.rolesLoaded = Array.isArray(response.data.role_options)
        this.allowedRoles = allowedRolesFromRows(response.data.allowed_roles || [])
        this.roleOptions = response.data.role_options || []
        this.layout = []
        this.time_period_types = response.data.time_period_types

        for(let n in response.data.widgets){
          let time_period_type_id = response.data.widgets[n].time_period_type_id
          let start_date = response.data.widgets[n].start_date ? moment(response.data.widgets[n].start_date, "YYYY-MM-DD") : moment()
          let end_date = response.data.widgets[n].end_date ? moment(response.data.widgets[n].end_date, "YYYY-MM-DD") : moment()

          const widgetItem = {
            x: legacyGrid ? response.data.widgets[n].x * 2 : response.data.widgets[n].x,
            y: response.data.widgets[n].y,
            w: legacyGrid ? response.data.widgets[n].w * 2 : response.data.widgets[n].w,
            h: response.data.widgets[n].h,
            i: response.data.widgets[n].id,
            id: response.data.widgets[n].id,
            data: [], // Empty data initially
            label: response.data.widgets[n].label,
            title: response.data.widgets[n].title,
            highlight_color: response.data.widgets[n].highlight_color,
            widget_type_id: response.data.widgets[n].widget_type_id,
            chart_type_id: response.data.widgets[n].chart_type_id,
            metric_type_id: response.data.widgets[n].metric_type_id,
            table_id: response.data.widgets[n].table_id,
            field_id: response.data.widgets[n].field_id,
            data_type: response.data.widgets[n].data_type,
            is_all_time: Boolean(response.data.widgets[n].is_all_time),
            start_date: start_date,
            end_date: end_date,
            show_range_option: response.data.widgets[n].show_range_option,
            show_compare_option: response.data.widgets[n].show_compare_option !== undefined ? Boolean(response.data.widgets[n].show_compare_option) : true,
            activity_source_id: response.data.widgets[n].activity_source_id,
            event_limit: response.data.widgets[n].event_limit,
            time_period_type_id: Number(time_period_type_id),
            period: _.find(this.time_period_types, {id: time_period_type_id}),
            y_axis_dimension: _.find(response.data.widgets[n].table_id === 17 ? this.purchaseMeasurements : this.measurements, {label: response.data.widgets[n].y_axis_dimension}),
            game_id: response.data.widgets[n].game_id || null,
            game_name: response.data.widgets[n].game_name || null,
            group_by_game: Boolean(response.data.widgets[n].group_by_game),
            company_user_id: response.data.widgets[n].company_user_id || null,
            group_by_user: Boolean(response.data.widgets[n].group_by_user),
            group_by_field: response.data.widgets[n].group_by_field || null,
            company_user_name: response.data.widgets[n].company_user_name || null,
            custom_field_id: response.data.widgets[n].custom_field_id || null,
            compare_mode: ['previous_periods', 'previous_years'].includes(response.data.widgets[n].default_compare_mode) ? response.data.widgets[n].default_compare_mode : 'none',
            compare_count: [2,3,4,5,6,7,8,9,10].includes(Number(response.data.widgets[n].default_compare_count)) ? Number(response.data.widgets[n].default_compare_count) : 2,
            comparison: null,
            granularity: ['day', 'week', 'month'].includes(response.data.widgets[n].default_granularity) ? response.data.widgets[n].default_granularity : 'auto',
            applied_granularity: 'day',
            default_granularity: response.data.widgets[n].default_granularity || 'auto',
            default_compare_mode: response.data.widgets[n].default_compare_mode || 'none',
            default_compare_count: Number(response.data.widgets[n].default_compare_count) || 2,
            field_name: response.data.widgets[n].field_name || null,
            show_description_label: response.data.widgets[n].show_description_label !== undefined ? Boolean(response.data.widgets[n].show_description_label) : true,
            allow_export: response.data.widgets[n].allow_export !== undefined ? Boolean(response.data.widgets[n].allow_export) : true,
            purchase_types: response.data.widgets[n].purchase_types || null,
            purchase_item_filters: response.data.widgets[n].purchase_item_filters || null,
            scope: response.data.widgets[n].scope === 'company' ? 'company' : 'group',
            company_group_ids: Array.isArray(response.data.widgets[n].company_group_ids) ? response.data.widgets[n].company_group_ids : [],
            new: false
          }

          this.layout.push(widgetItem);
        }

        if(this.allGroups.length === 0 && response.data.widgets && Object.values(response.data.widgets).some(w => w.scope === 'company')){
          this.getGroups()
        }

        if(legacyGrid && this.layout.length > 0){
          this.updateLayout()
        }
        // Layout events may persist from here on (after the grid has rendered this layout).
        this.$nextTick(() => { this.layoutReady = true })

        this.widget_types = response.data.widget_types
        this.chart_types = response.data.chart_types
        this.all_metric_types = response.data.metric_types
        this.sources = response.data.data_source_tables
        this.all_fields = response.data.data_source_fields
        this.activity_sources = response.data.activity_sources

        await this.loadAllWidgets()

      } catch(err) {
        this.loading = false
        console.log(err)
        if (err.response && err.response.status == 401) {
          // The dashboard id is not visible under the active group (typically the group was
          // switched while editing): send the user to the group's dashboard list instead of Forbidden.
          this.$notify({
            type: 'warning',
            message: 'That dashboard belongs to another group. Showing the dashboards for the selected group.',
            icon: 'tim-icons icon-alert-circle-exc'
          })
          this.$router.push('/dashboards')
        } else if(err.response && err.response.status == 400){
          this.$notify({ type: 'danger', message: typeof err.response.data === 'string' ? err.response.data : 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        }
      }
    },
    async getUsers(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/company-users'

      this.axios.get(url, config)
        .then(response => {
          this.users = response.data.company_users
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
          }
        })

    },
    async getPaymentMethods(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/payment-methods'

      try {
        let response = await this.axios.get(url, config)
        if(response.data.payment_methods && response.data.payment_methods.length > 0){
          // Label is the group's configured method name; value is the stored
          // payments.type code so the saved filter matches actual payment records.
          this.paymentMethodOptions = response.data.payment_methods.map(pm => ({
            value: this.getMethodKey(pm.name),
            label: pm.name
          }))
        }
      } catch(err) {
        console.log(err)
      }
    },
    getMethodKey(name){
      if(!name) return ''
      // Mirror the write path: built-in methods map to their lowercase type
      // codes, custom methods are stored under their own name.
      const mapping = {
        'pos payment': 'pos',
        'card': 'card',
        'cash': 'cash',
        'gift card': 'gift_card',
        'customer credit': 'customer_credit'
      }
      const lowerName = name.toLowerCase()
      return mapping[lowerName] || name
    },
    formatMethodLabel(value){
      // Fallback label for a stored method code with no configured option:
      // underscores to spaces, capitalize the first letter.
      if(!value) return value
      const spaced = String(value).replace(/_/g, ' ')
      return spaced.charAt(0).toUpperCase() + spaced.slice(1)
    },
    async getGames(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games'

      this.axios.get(url, config)
        .then(response => {
          // Handle different response structures
          if (response.data.games) {
            this.games = response.data.games.filter(g => g.archived === 0 || g.archived === false)
          } else if (Array.isArray(response.data)) {
            this.games = response.data.filter(g => g.archived === 0 || g.archived === false)
          } else {
            console.warn('Unexpected games response structure:', response.data)
            this.games = []
          }
        })
        .catch(err => {
          console.error('Error fetching games:', err)
          console.error('Error response:', err.response)
          this.games = []
        })
    },
    async getItems(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId +
        '/inventory?count=1000&offset=0&item_name=&category=&unit_price=&available=&purchased=&internal_only='

      this.axios.get(url, config)
        .then(response => {
          if (response.data.items) {
            this.items = response.data.items.filter(i => !i.is_archived)
          } else if (Array.isArray(response.data)) {
            this.items = response.data.filter(i => !i.is_archived)
          } else {
            this.items = []
          }
        })
        .catch(err => {
          console.error('Error fetching items:', err)
          this.items = []
        })
    },
    async getGiftCardTypes(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/gift-card-types'

      this.axios.get(url, config)
        .then(response => {
          if (response.data.gift_card_types) {
            this.gift_card_types = response.data.gift_card_types
          } else if (Array.isArray(response.data)) {
            this.gift_card_types = response.data
          } else {
            this.gift_card_types = []
          }
        })
        .catch(err => {
          console.error('Error fetching gift card types:', err)
          this.gift_card_types = []
        })
    },
    activityFields(item){
      // Booking activity rows carry the booked event; transaction activity rows carry the order
      // number. Other sources keep the three original columns.
      // Fixed column widths (table-layout: fixed) so every cell has a bounded width for
      // v-fit-text to shrink into; the action column takes whatever remains.
      const col = (key, label, width) => ({ key, label, thStyle: width ? { width } : undefined })
      const time = col('time_of_event', 'Time', '19%')
      const action = col('action', 'Action')
      const by = col('performed_by', 'Performed by', '16%')
      const source = Number(item.activity_source_id)
      if(source === 2) return [time, col('booked_event', 'Booked event', '26%'), action, by]
      if(source === 3) return [time, col('transaction', 'Transaction', '17%'), action, by]
      return [time, action, by]
    },
    activityEvent(row){
      let group = null
      try { group = JSON.parse(localStorage.getItem('group')) } catch(e) { group = null }
      return describeActivityEvent(row, group)
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onViewportChange)
  },
  mounted() {
    if(!this.hasPermissions('dashboards_update')){
      this.$router.push('/dashboards')
    }
    this.onViewportChange()
    window.addEventListener('resize', this.onViewportChange)
    this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    this.date_format_cal = this.date_format
    this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
    this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
    this.getUsers()
    this.getGames()
    this.getPaymentMethods()
    this.getItems()
    this.getGiftCardTypes()
    this.getDashboard(1)
  }
}
</script>

<style scoped>
.cf-stats__loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  font-size: 13px;
  color: rgba(125, 125, 125, 0.9);
}
.white-content .ai-trigger {
  color: #b23d9c;
}
.ai-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(225, 78, 202, 0.1);
  border: 1px solid rgba(225, 78, 202, 0.35);
  color: #e14eca;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-right: 8px;
  transition: background 0.2s ease;
}
.ai-trigger:hover,
.ai-trigger--active {
  background: rgba(225, 78, 202, 0.2);
}
.ai-trigger__beta {
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(225, 78, 202, 0.2);
  border-radius: 3px;
  padding: 1px 4px;
}
@media (max-width: 575.98px) {
  .ai-trigger__label { display: none; }
}
.cf-stats__spinner {
  color: #e14eca;
  font-size: 14px;
}
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
}
/* Match base-input's tamed label token (12px / dim) so the card-field labels
   sit consistently with the rest of the form, with a plain (non-red) asterisk. */
.widget-field-label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  margin-bottom: 6px;
  color: rgba(255, 255, 255, 0.85);
}
.white-content .widget-field-label {
  color: #525f7f;
}
/* Switch + label on one aligned, full-width row (was stacking/wrapping awkwardly). */
.toggle-field {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}
.toggle-field label,
.toggle-field span {
  margin-bottom: 0;
  flex: 1;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}
.white-content .toggle-field label,
.white-content .toggle-field span {
  color: #525f7f;
}
/* Trim the form-group bottom margin inside the time-range controls so the
   section doesn't carry a big empty gap below the date pickers. */
.time-range-controls ::v-deep .form-group {
  margin-bottom: 0.25rem;
}
.vue-grid-layout {
  background: inherit !important;
}

.vue-grid-item:not(.vue-grid-placeholder) {
  background: inherit !important;
}

.vue-grid-item .resizing {
  opacity: 0.9;
}

.vue-grid-item .static {
  background: #cce;
}

.vue-grid-item .text {
  font-size: 24px;
  text-align: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  height: 100%;
  width: 100%;
}

.vue-grid-item .no-drag {
  height: 100%;
  width: 100%;
}

.vue-grid-item .minMax {
  font-size: 12px;
}

.vue-grid-item .add {
  cursor: pointer;
}

.vue-draggable-handle {
  position: absolute;
  width: 20px;
  height: 20px;
  top: 0;
  left: 0;
  background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10'><circle cx='5' cy='5' r='5' fill='#999999'/></svg>") no-repeat;
  background-position: bottom right;
  padding: 0 8px 8px 0;
  background-repeat: no-repeat;
  background-origin: content-box;
  box-sizing: border-box;
  cursor: pointer;
}

::v-deep(.vue-grid-item > .vue-resizable-handle) {
  all: unset !important;  /* Reset all default styles */
  position: absolute !important;
  bottom: 5px !important; /* Move 3px above the corner */
  right: 5px !important; /* Move 3px from the right */
  width: 8px !important; /* Define a small hit area */
  height: 8px !important;
  cursor: se-resize !important;
}

::v-deep(.vue-grid-item > .vue-resizable-handle::before),
::v-deep(.vue-grid-item > .vue-resizable-handle::after) {
  content: "" !important;
  position: absolute !important;
  background-color: #e14eca !important; /* Color of the lines */
}

/* Horizontal Line */
::v-deep(.vue-grid-item > .vue-resizable-handle::before) {
  width: 8px !important;
  height: 2px !important;
  bottom: 0 !important;
  left: 0 !important;
}

/* Vertical Line */
::v-deep(.vue-grid-item > .vue-resizable-handle::after) {
  width: 2px !important;
  height: 8px !important;
  bottom: 0 !important;
  right: 0 !important;
}

::v-deep(.vue-grid-item.vue-grid-placeholder) {
  background: #ba54f5 !important; /* Change to your preferred color */
  opacity: 0.1 !important; /* Adjust transparency if needed */
  border-radius: 3px;
}

.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
